// AI Framework - Full tree (v3)
// Structured around the user's comprehensive taxonomy.
// Type codes: T=Free/Open, R=Requires reg, M=Paid/Freemium, D=Dangerous/Underground

// Helper to keep entries compact
const L = (name, url, type = 'T') => ({ name, url, type });
const S = (name, children) => ({ name, children });

export const aiFrameworkTree = {
  name: 'AI Framework',
  children: [
    // ────────────────────── POPULAR TOOLKITS (curated) ──────────────────────
    S('Popular Toolkits', [
      S('Content Generation & Copywriting', [
        L('Writesonic', 'https://writesonic.com/', 'M'),
        L('Jasper AI', 'https://www.jasper.ai/', 'M'),
        L('Copy.ai', 'https://www.copy.ai/', 'M'),
        L('Anyword', 'https://anyword.com/', 'M'),
      ]),
      S('Video, Voice & Audio AI', [
        L('Pictory AI', 'https://pictory.ai/', 'M'),
        L('Synthesia', 'https://www.synthesia.io/', 'M'),
        L('ElevenLabs', 'https://elevenlabs.io/', 'M'),
        L('Murf AI', 'https://murf.ai/', 'M'),
      ]),
      S('AI Chatbots & Voice Agents', [
        L('Chatbase', 'https://www.chatbase.co/', 'M'),
        L('Synthflow AI', 'https://synthflow.ai/', 'M'),
        L('CustomGPT.ai', 'https://customgpt.ai/', 'M'),
      ]),
      S('SEO & Search Intelligence', [
        L('Surfer SEO', 'https://surferseo.com/', 'M'),
        L('Semrush', 'https://www.semrush.com/', 'M'),
      ]),
      S('Workspace & Design', [
        L('Notion AI', 'https://www.notion.so/product/ai', 'M'),
        L('Reclaim.ai', 'https://reclaim.ai/', 'M'),
        L('Gamma', 'https://gamma.app/', 'M'),
      ]),
      S('Marketing & Operations Automation', [
        L('GetResponse', 'https://www.getresponse.com/', 'M'),
        L('HubSpot', 'https://www.hubspot.com/', 'M'),
        L('Make', 'https://www.make.com/', 'M'),
        L('AdCreative.ai', 'https://www.adcreative.ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 1. TEXT & WRITING ──────────────────────────
    S('Text & Writing', [
      S('Writing assistants', [
        L('ChatGPT', 'https://chat.openai.com/', 'T'),
        L('Claude', 'https://claude.ai/', 'T'),
        L('Gemini', 'https://gemini.google.com/', 'T'),
        L('Notion AI', 'https://www.notion.so/product/ai', 'M'),
        L('Lex.page', 'https://lex.page/', 'T'),
        L('Mem.ai', 'https://get.mem.ai/', 'M'),
      ]),
      S('Copywriting', [
        L('Jasper', 'https://www.jasper.ai/', 'M'),
        L('Copy.ai', 'https://www.copy.ai/', 'M'),
        L('Writesonic', 'https://writesonic.com/', 'M'),
        L('Anyword', 'https://anyword.com/', 'M'),
        L('Rytr', 'https://rytr.me/', 'T'),
      ]),
      S('Email generation', [
        L('Lavender.ai', 'https://www.lavender.ai/', 'M'),
        L('SmartWriter', 'https://www.smartwriter.ai/', 'M'),
        L('Flowrite', 'https://www.flowrite.com/', 'M'),
        L('Superhuman AI', 'https://superhuman.com/ai', 'M'),
      ]),
      S('Story writing', [
        L('Sudowrite', 'https://www.sudowrite.com/', 'M'),
        L('NovelAI', 'https://novelai.net/', 'M'),
        L('NovelCrafter', 'https://novelcrafter.com/', 'M'),
        L('Squibler', 'https://www.squibler.io/', 'M'),
        L('ShortlyAI', 'https://www.shortlyai.com/', 'M'),
      ]),
      S('Script writing', [
        L('Squibler Script', 'https://www.squibler.io/', 'M'),
        L('SoundDraw Script', 'https://boords.com/scriptwriter', 'M'),
        L('Toolsaday Script', 'https://toolsaday.com/writing/script-writer', 'T'),
        L('Filmustage', 'https://filmustage.com/', 'M'),
      ]),
      S('Technical writing', [
        L('Scribe', 'https://scribehow.com/', 'M'),
        L('Document360 AI', 'https://document360.com/', 'M'),
        L('Guidde', 'https://www.guidde.com/', 'M'),
        L('Mintlify', 'https://mintlify.com/', 'T'),
      ]),
      S('Resume builders', [
        L('Kickresume', 'https://www.kickresume.com/', 'M'),
        L('Rezi.ai', 'https://www.rezi.ai/', 'M'),
        L('Enhancv AI', 'https://enhancv.com/', 'M'),
        L('Teal', 'https://www.tealhq.com/', 'M'),
        L('Resume Worded', 'https://resumeworded.com/', 'M'),
      ]),
      S('Translation', [
        L('DeepL', 'https://www.deepl.com/', 'T'),
        L('Google Translate', 'https://translate.google.com/', 'T'),
        L('Reverso', 'https://www.reverso.net/', 'T'),
        L('Lokalise AI', 'https://lokalise.com/ai', 'M'),
      ]),
      S('Grammar correction', [
        L('Grammarly', 'https://www.grammarly.com/', 'T'),
        L('LanguageTool', 'https://languagetool.org/', 'T'),
        L('ProWritingAid', 'https://prowritingaid.com/', 'M'),
        L('Ginger', 'https://www.gingersoftware.com/', 'T'),
      ]),
      S('Summarization', [
        L('Google NotebookLM', 'https://notebooklm.google.com/', 'T'),
        L('Wordtune Read', 'https://www.wordtune.com/read', 'T'),
        L('Genei', 'https://www.genei.io/', 'M'),
        L('Recall', 'https://www.recall.ai/', 'M'),
        L('SMMRY', 'https://smmry.com/', 'T'),
      ]),
      S('Research assistants', [
        L('Perplexity', 'https://www.perplexity.ai/', 'T'),
        L('Elicit', 'https://elicit.com/', 'T'),
        L('Consensus', 'https://consensus.app/', 'T'),
        L('SciSpace', 'https://scispace.com/', 'T'),
        L('Undermind', 'https://www.undermind.ai/', 'M'),
      ]),
      S('Prompt generators', [
        L('PromptPerfect', 'https://promptperfect.jina.ai/', 'M'),
        L('AIPRM', 'https://www.aiprm.com/', 'T'),
        L('PromptHero', 'https://prompthero.com/', 'T'),
        L('FlowGPT', 'https://flowgpt.com/', 'T'),
      ]),
    ]),

    // ────────────────────────── 2. IMAGE GENERATION ──────────────────────────
    S('Image Generation', [
      S('Photorealistic images', [
        L('Midjourney v6.1', 'https://www.midjourney.com/', 'M'),
        L('FLUX.1', 'https://blackforestlabs.ai/', 'T'),
        L('Ideogram 2.0', 'https://ideogram.ai/', 'T'),
        L('Krea', 'https://www.krea.ai/', 'M'),
        L('Magnific', 'https://magnific.ai/', 'M'),
      ]),
      S('Digital art', [
        L('Midjourney', 'https://www.midjourney.com/', 'M'),
        L('Leonardo.AI', 'https://leonardo.ai/', 'T'),
        L('NightCafe', 'https://creator.nightcafe.studio/', 'T'),
        L('Playground', 'https://playground.com/', 'T'),
        L('Artbreeder', 'https://www.artbreeder.com/', 'T'),
      ]),
      S('Concept art', [
        L('Scenario', 'https://www.scenario.com/', 'M'),
        L('Leonardo.AI', 'https://leonardo.ai/', 'T'),
        L('Krea', 'https://www.krea.ai/', 'M'),
        L('Layer AI', 'https://layer.ai/', 'M'),
      ]),
      S('Logo creation', [
        L('Looka', 'https://looka.com/', 'M'),
        L('Brandmark', 'https://brandmark.io/', 'M'),
        L('LogoAI', 'https://www.logoai.com/', 'M'),
        L('Namelix', 'https://namelix.com/', 'T'),
      ]),
      S('Product mockups', [
        L('Booth.ai', 'https://booth.ai/', 'M'),
        L('Flair.ai', 'https://flair.ai/', 'M'),
        L('Pebblely', 'https://pebblely.com/', 'M'),
        L('Mokker AI', 'https://mokker.ai/', 'M'),
      ]),
      S('Interior design', [
        L('REimagineHome', 'https://www.reimaginehome.ai/', 'M'),
        L('Interior AI', 'https://interiorai.com/', 'M'),
        L('Planner 5D AI', 'https://planner5d.com/', 'M'),
        L('Collov', 'https://collov.ai/', 'M'),
        L('Spacely AI', 'https://www.spacely.ai/', 'M'),
      ]),
      S('Architecture visualization', [
        L('ArkoAI', 'https://arko.ai/', 'M'),
        L('Maket', 'https://www.maket.ai/', 'M'),
        L('Veras (Evolvelab)', 'https://www.evolvelab.io/veras', 'M'),
        L('Autodesk Forma', 'https://www.autodesk.com/products/forma', 'M'),
      ]),
      S('Character design', [
        L('Scenario', 'https://www.scenario.com/', 'M'),
        L('Character Creator (SD)', 'https://civitai.com/', 'D'),
        L('Layer AI', 'https://layer.ai/', 'M'),
        L('CharacterHub', 'https://www.characterhub.org/', 'T'),
      ]),
      S("Children's books", [
        L('Storybird AI', 'https://storybird.ai/', 'M'),
        L('BookBildr', 'https://www.bookbildr.com/', 'M'),
        L("Storytime AI", 'https://www.storytime-ai.com/', 'M'),
        L('Diffit', 'https://beta.diffit.me/', 'T'),
      ]),
      S('Fashion design', [
        L('CALA', 'https://ca.la/', 'M'),
        L('Vue.ai', 'https://vue.ai/', 'M'),
        L('Off/Script', 'https://www.offscript.com/', 'T'),
        L('Refabric', 'https://refabric.com/', 'M'),
      ]),
      S('Tattoo design', [
        L('BlackInk AI', 'https://blackink.ai/', 'M'),
        L('InkGenerator', 'https://www.inkgenerator.com/', 'M'),
        L('TattoosAI', 'https://tattoosai.com/', 'M'),
        L('Tattoo.AI', 'https://tattoo.ai/', 'M'),
      ]),
      S('Infographics', [
        L('Piktochart AI', 'https://piktochart.com/ai-infographic-generator/', 'M'),
        L('Venngage AI', 'https://venngage.com/ai-tools/infographic-generator', 'M'),
        L('Visme AI', 'https://www.visme.co/ai-infographic-generator/', 'M'),
        L('Napkin AI', 'https://www.napkin.ai/', 'T'),
      ]),
      S('Comic creation', [
        L('AI Comic Factory', 'https://aicomicfactory.app/', 'T'),
        L('Comicgen', 'https://www.comicgen.ai/', 'T'),
        L('Dashtoon', 'https://dashtoon.com/', 'T'),
        L('Comixify', 'https://comixify.ai/', 'M'),
      ]),
      S('Coloring pages', [
        L('Coloring Book AI', 'https://coloringbook.ai/', 'M'),
        L('Hello Kids AI', 'https://www.hellokids.com/', 'T'),
        L('MagicColoringPages', 'https://magiccoloringpages.com/', 'T'),
      ]),
      S('Icons & UI graphics', [
        L('Iconify AI', 'https://icons8.com/ai-icon-generator', 'M'),
        L('Recraft (icons)', 'https://www.recraft.ai/', 'T'),
        L('IconifyAI.com', 'https://iconifyai.com/', 'M'),
        L('Magician for Figma', 'https://magician.design/', 'M'),
      ]),
    ]),

    // ────────────────────────── 3. VIDEO GENERATION ──────────────────────────
    S('Video Generation', [
      S('Text-to-video', [
        L('Sora (OpenAI)', 'https://openai.com/sora', 'M'),
        L('Runway Gen-3', 'https://runwayml.com/', 'M'),
        L('Pika 2.0', 'https://pika.art/', 'T'),
        L('Kling AI 1.6', 'https://kling.kuaishou.com/', 'T'),
        L('Luma Dream Machine', 'https://lumalabs.ai/dream-machine', 'T'),
        L('Hailuo AI (MiniMax)', 'https://hailuoai.video/', 'T'),
        L('Veo 2 (Google)', 'https://deepmind.google/technologies/veo/', 'R'),
        L('Hunyuan Video', 'https://aivideo.hunyuan.tencent.com/', 'T'),
      ]),
      S('Image-to-video', [
        L('Runway Gen-3', 'https://runwayml.com/', 'M'),
        L('Kling AI', 'https://kling.kuaishou.com/', 'T'),
        L('Stable Video Diffusion', 'https://stability.ai/stable-video', 'T'),
        L('LivePortrait', 'https://liveportrait.github.io/', 'T'),
        L('Luma Ray', 'https://lumalabs.ai/dream-machine', 'T'),
      ]),
      S('AI avatars', [
        L('HeyGen', 'https://www.heygen.com/', 'M'),
        L('Synthesia', 'https://www.synthesia.io/', 'M'),
        L('Colossyan', 'https://www.colossyan.com/', 'M'),
        L('Elai.io', 'https://elai.io/', 'M'),
        L('DeepBrain AI', 'https://www.deepbrain.io/', 'M'),
      ]),
      S('Talking heads', [
        L('D-ID', 'https://www.d-id.com/', 'M'),
        L('Hedra Character-1', 'https://www.hedra.com/', 'T'),
        L('Captions', 'https://www.captions.ai/', 'T'),
        L('LivePortrait', 'https://liveportrait.github.io/', 'T'),
      ]),
      S('Commercials', [
        L('Waymark', 'https://waymark.com/', 'M'),
        L('Creatify', 'https://creatify.ai/', 'M'),
        L('Nova AI', 'https://wearenova.ai/', 'M'),
        L('AdCreative.ai', 'https://www.adcreative.ai/', 'M'),
      ]),
      S('Music videos', [
        L('Neural Frames', 'https://www.neuralframes.com/', 'M'),
        L('Kaiber', 'https://kaiber.ai/', 'M'),
        L('Wombo Dream', 'https://dream.ai/', 'T'),
        L('AudioReactive (Runway)', 'https://runwayml.com/', 'M'),
      ]),
      S('Cinematic scenes', [
        L('LTX Studio', 'https://ltx.studio/', 'R'),
        L('Runway Gen-3', 'https://runwayml.com/', 'M'),
        L('Kling AI', 'https://kling.kuaishou.com/', 'T'),
        L('Sora', 'https://openai.com/sora', 'M'),
      ]),
      S('Animation', [
        L('Cascadeur', 'https://cascadeur.com/', 'T'),
        L('Animaker AI', 'https://www.animaker.com/', 'M'),
        L('Vyond', 'https://www.vyond.com/', 'M'),
        L('Toonify (Vidu)', 'https://www.vidu.studio/', 'T'),
      ]),
      S('Explainer videos', [
        L('Steve.AI', 'https://www.steve.ai/', 'M'),
        L('Pictory', 'https://pictory.ai/', 'M'),
        L('Lumen5', 'https://lumen5.com/', 'M'),
        L('Fliki', 'https://fliki.ai/', 'T'),
      ]),
      S('Shorts / Reels / TikToks', [
        L('Opus Clip', 'https://www.opus.pro/', 'M'),
        L('AutoShorts.ai', 'https://autoshorts.ai/', 'M'),
        L('Vizard', 'https://vizard.ai/', 'M'),
        L('Munch', 'https://www.getmunch.com/', 'M'),
        L('Klap', 'https://klap.app/', 'M'),
      ]),
      S('Movie trailers', [
        L('LTX Studio', 'https://ltx.studio/', 'R'),
        L('Runway (Cinematic)', 'https://runwayml.com/', 'M'),
        L('InVideo', 'https://invideo.io/', 'T'),
        L('FlexClip AI', 'https://www.flexclip.com/', 'M'),
      ]),
      S('Lip-sync videos', [
        L('Sync.so', 'https://sync.so/', 'T'),
        L('Rask AI', 'https://www.rask.ai/', 'M'),
        L('HeyGen Video Translate', 'https://www.heygen.com/video-translate', 'M'),
        L('Wav2Lip', 'https://github.com/Rudrabha/Wav2Lip', 'T'),
      ]),
      S('Motion graphics', [
        L('Runway Motion Brush', 'https://runwayml.com/', 'M'),
        L('Cavalry AI', 'https://cavalry.scenegroup.co/', 'M'),
        L('Rive', 'https://rive.app/', 'T'),
        L('MotionGPT', 'https://motion-gpt.github.io/', 'T'),
      ]),
    ]),

    // ────────────────────────── 4. MUSIC & AUDIO ──────────────────────────
    S('Music & Audio', [
      S('Full songs', [
        L('Suno v4', 'https://suno.com/', 'T'),
        L('Udio', 'https://www.udio.com/', 'T'),
        L('Riffusion', 'https://riffusion.com/', 'T'),
        L('Mubert', 'https://mubert.com/', 'T'),
      ]),
      S('Instrumentals', [
        L('AIVA', 'https://www.aiva.ai/', 'T'),
        L('Soundraw', 'https://soundraw.io/', 'M'),
        L('Beatoven', 'https://www.beatoven.ai/', 'M'),
        L('Stable Audio', 'https://stableaudio.com/', 'T'),
      ]),
      S('Beats', [
        L('WavTool', 'https://wavtool.com/', 'M'),
        L('Loudly', 'https://www.loudly.com/', 'T'),
        L('Boomy', 'https://boomy.com/', 'T'),
      ]),
      S('Lyrics', [
        L('LyricStudio', 'https://lyricstudio.net/', 'M'),
        L('These Lyrics Do Not Exist', 'https://theselyricsdonotexist.com/', 'T'),
        L('Rytr Lyrics', 'https://rytr.me/', 'T'),
      ]),
      S('Singing voices', [
        L('Suno', 'https://suno.com/', 'T'),
        L('Kits.AI', 'https://www.kits.ai/', 'M'),
        L('Voicemod', 'https://www.voicemod.net/', 'T'),
        L('Musicfy', 'https://www.musicfy.lol/', 'M'),
      ]),
      S('Voice cloning', [
        L('ElevenLabs', 'https://elevenlabs.io/', 'M'),
        L('PlayHT', 'https://play.ht/', 'M'),
        L('Cartesia', 'https://cartesia.ai/', 'R'),
        L('Resemble AI', 'https://www.resemble.ai/', 'M'),
        L('Fish Speech', 'https://fish.audio/', 'T'),
      ]),
      S('Sound effects', [
        L('ElevenLabs SFX', 'https://elevenlabs.io/sound-effects', 'M'),
        L('AudioGen (Meta)', 'https://felixkreuk.github.io/audiogen/', 'T'),
        L('Optimizer AI SFX', 'https://www.optimizerai.xyz/', 'T'),
      ]),
      S('Podcasts', [
        L('NotebookLM Audio', 'https://notebooklm.google.com/', 'T'),
        L('Podcastle', 'https://podcastle.ai/', 'M'),
        L('Adobe Podcast', 'https://podcast.adobe.com/', 'T'),
        L('Riverside AI', 'https://riverside.fm/', 'M'),
      ]),
      S('Audio cleanup', [
        L('Adobe Podcast Enhance', 'https://podcast.adobe.com/enhance', 'T'),
        L('Krisp', 'https://krisp.ai/', 'M'),
        L('Auphonic', 'https://auphonic.com/', 'M'),
        L('AudioShake', 'https://www.audioshake.ai/', 'M'),
      ]),
      S('Audio mastering', [
        L('LANDR', 'https://www.landr.com/', 'M'),
        L('CloudBounce', 'https://www.cloudbounce.com/', 'M'),
        L('eMastered', 'https://emastered.com/', 'M'),
      ]),
      S('Music separation', [
        L('LALAL.AI', 'https://www.lalal.ai/', 'M'),
        L('Moises', 'https://moises.ai/', 'M'),
        L('Vocal Remover (Ultimate VR)', 'https://github.com/Anjok07/ultimatevocalremovergui', 'T'),
        L('Spleeter (Deezer)', 'https://github.com/deezer/spleeter', 'T'),
      ]),
      S('DJ mixing', [
        L('AlgoRiddim djay', 'https://www.algoriddim.com/', 'M'),
        L('MixMeister', 'https://www.mixmeister.com/', 'M'),
        L('Serato Stems', 'https://serato.com/stems', 'M'),
      ]),
    ]),

    // ────────────────────────── 5. SPEECH ──────────────────────────
    S('Speech', [
      S('Text-to-speech', [
        L('ElevenLabs', 'https://elevenlabs.io/', 'M'),
        L('OpenAI TTS', 'https://platform.openai.com/docs/guides/text-to-speech', 'R'),
        L('PlayHT', 'https://play.ht/', 'M'),
        L('Murf', 'https://murf.ai/', 'M'),
        L('Speechify', 'https://speechify.com/', 'M'),
        L('Coqui XTTS', 'https://github.com/coqui-ai/TTS', 'T'),
      ]),
      S('Speech-to-text', [
        L('Whisper (OpenAI)', 'https://github.com/openai/whisper', 'T'),
        L('AssemblyAI', 'https://www.assemblyai.com/', 'M'),
        L('Deepgram', 'https://deepgram.com/', 'M'),
        L('Rev.ai', 'https://www.rev.ai/', 'M'),
        L('Otter.ai', 'https://otter.ai/', 'M'),
      ]),
      S('Real-time translation', [
        L('DeepL Voice', 'https://www.deepl.com/en/voice', 'M'),
        L('Google Translate Live', 'https://translate.google.com/', 'T'),
        L('SeamlessM4T (Meta)', 'https://ai.meta.com/blog/seamless-m4t/', 'T'),
        L('KUDO AI', 'https://kudoway.com/', 'M'),
      ]),
      S('Accent conversion', [
        L('Krisp Accent Convert', 'https://krisp.ai/accent-conversion/', 'M'),
        L('Sanas', 'https://www.sanas.ai/', 'M'),
        L('BoldVoice', 'https://boldvoice.com/', 'M'),
      ]),
      S('Voice dubbing', [
        L('ElevenLabs Dubbing', 'https://elevenlabs.io/dubbing', 'M'),
        L('Rask AI', 'https://www.rask.ai/', 'M'),
        L('HeyGen Video Translate', 'https://www.heygen.com/video-translate', 'M'),
        L('Dubverse', 'https://dubverse.ai/', 'M'),
      ]),
      S('Audiobook narration', [
        L('ElevenLabs Reader', 'https://elevenlabs.io/reader', 'M'),
        L('Speechki', 'https://speechki.org/', 'M'),
        L('Play.ht Audiobook', 'https://play.ht/audiobook-creator/', 'M'),
        L('Audible AI narration', 'https://www.audible.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 6. CODING ──────────────────────────
    S('Coding', [
      S('Code generation', [
        L('Cursor', 'https://cursor.com/', 'M'),
        L('Claude Code', 'https://www.anthropic.com/claude-code', 'M'),
        L('GitHub Copilot', 'https://github.com/features/copilot', 'M'),
        L('Windsurf', 'https://codeium.com/windsurf', 'T'),
        L('Aider', 'https://aider.chat/', 'T'),
      ]),
      S('Code completion', [
        L('GitHub Copilot', 'https://github.com/features/copilot', 'M'),
        L('Codeium', 'https://codeium.com/', 'T'),
        L('Tabnine', 'https://www.tabnine.com/', 'M'),
        L('Amazon Q Developer', 'https://aws.amazon.com/q/developer/', 'M'),
        L('Supermaven', 'https://supermaven.com/', 'M'),
      ]),
      S('Debugging', [
        L('Cursor Debug', 'https://cursor.com/', 'M'),
        L('Sentry AI', 'https://sentry.io/', 'M'),
        L('Traceloop', 'https://www.traceloop.com/', 'M'),
        L('Snyk DeepCode', 'https://snyk.io/product/snyk-code/', 'M'),
      ]),
      S('Refactoring', [
        L('Sourcegraph Cody', 'https://sourcegraph.com/cody', 'M'),
        L('Continue.dev', 'https://www.continue.dev/', 'T'),
        L('Cline', 'https://github.com/cline/cline', 'T'),
        L('Roo Code', 'https://roocode.com/', 'T'),
      ]),
      S('Documentation', [
        L('Mintlify', 'https://mintlify.com/', 'T'),
        L('Swimm', 'https://swimm.io/', 'M'),
        L('Document360 AI', 'https://document360.com/', 'M'),
      ]),
      S('Website creation', [
        L('v0 by Vercel', 'https://v0.dev/', 'T'),
        L('Lovable', 'https://lovable.dev/', 'T'),
        L('Bolt.new', 'https://bolt.new/', 'T'),
        L('Emergent', 'https://emergent.sh/', 'T'),
      ]),
      S('App generation', [
        L('Emergent', 'https://emergent.sh/', 'T'),
        L('Replit Agent', 'https://replit.com/', 'T'),
        L('Softgen', 'https://softgen.ai/', 'T'),
        L('a0.dev', 'https://a0.dev/', 'T'),
      ]),
      S('API creation', [
        L('Cursor', 'https://cursor.com/', 'M'),
        L('Postman AI', 'https://www.postman.com/product/postbot/', 'M'),
        L('Hoppscotch AI', 'https://hoppscotch.io/', 'T'),
      ]),
      S('SQL generation', [
        L('Text2SQL.ai', 'https://www.text2sql.ai/', 'M'),
        L('AI2sql', 'https://www.ai2sql.io/', 'M'),
        L('SQL Chat', 'https://www.sqlchat.ai/', 'T'),
        L('Vanna.AI', 'https://vanna.ai/', 'T'),
      ]),
      S('Test generation', [
        L('CodiumAI', 'https://www.codium.ai/', 'M'),
        L('Diffblue Cover', 'https://www.diffblue.com/', 'M'),
        L('Meticulous', 'https://www.meticulous.ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 7. WEBSITE BUILDERS ──────────────────────────
    S('Website Builders', [
      S('Landing pages', [
        L('Framer AI', 'https://www.framer.com/ai', 'M'),
        L('Unbounce Smart Copy', 'https://unbounce.com/product/smart-copy/', 'M'),
        L('Landingsite', 'https://landingsite.ai/', 'M'),
        L('Durable', 'https://durable.co/', 'M'),
      ]),
      S('Business websites', [
        L('Wix ADI', 'https://www.wix.com/', 'M'),
        L('Hostinger AI Builder', 'https://www.hostinger.com/ai-website-builder', 'M'),
        L('10Web', 'https://10web.io/', 'M'),
        L('B12', 'https://www.b12.io/', 'M'),
      ]),
      S('Portfolios', [
        L('Framer AI', 'https://www.framer.com/ai', 'M'),
        L('Dorik AI', 'https://dorik.com/', 'M'),
        L('Copyfolio', 'https://copyfolio.com/', 'M'),
      ]),
      S('E-commerce stores', [
        L('Shopify Magic', 'https://www.shopify.com/magic', 'M'),
        L('Debutify AI', 'https://debutify.com/', 'M'),
        L('BigCommerce AI', 'https://www.bigcommerce.com/', 'M'),
      ]),
      S('Blogs', [
        L('Journalist AI', 'https://tryjournalist.com/', 'M'),
        L('ZimmWriter', 'https://zimmwriter.com/', 'M'),
        L('Byword', 'https://byword.ai/', 'M'),
        L('AutoBlogging.ai', 'https://autoblogging.ai/', 'M'),
      ]),
      S('Dashboards', [
        L('Rows AI', 'https://rows.com/', 'T'),
        L('Retool AI', 'https://retool.com/ai', 'M'),
        L('Softr AI', 'https://www.softr.io/', 'M'),
        L('Glide', 'https://www.glideapps.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 8. APP BUILDERS ──────────────────────────
    S('App Builders', [
      S('Mobile apps', [
        L('FlutterFlow AI', 'https://flutterflow.io/', 'M'),
        L('a0.dev (React Native)', 'https://a0.dev/', 'T'),
        L('Draftbit', 'https://draftbit.com/', 'M'),
        L('Adalo', 'https://www.adalo.com/', 'M'),
      ]),
      S('Desktop apps', [
        L('Electron + Cursor', 'https://cursor.com/', 'M'),
        L('AppMaster', 'https://appmaster.io/', 'M'),
      ]),
      S('Web apps', [
        L('Bolt.new', 'https://bolt.new/', 'T'),
        L('Lovable', 'https://lovable.dev/', 'T'),
        L('Emergent', 'https://emergent.sh/', 'T'),
        L('v0', 'https://v0.dev/', 'T'),
        L('Replit Agent', 'https://replit.com/', 'T'),
      ]),
      S('No-code apps', [
        L('Bubble AI', 'https://bubble.io/', 'M'),
        L('Softr', 'https://www.softr.io/', 'M'),
        L('Glide', 'https://www.glideapps.com/', 'M'),
      ]),
      S('Low-code apps', [
        L('Retool AI', 'https://retool.com/ai', 'M'),
        L('OutSystems', 'https://www.outsystems.com/', 'M'),
        L('Mendix Maia', 'https://www.mendix.com/platform/maia-ai/', 'M'),
      ]),
      S('Internal business tools', [
        L('Retool', 'https://retool.com/', 'M'),
        L('Airtable AI', 'https://www.airtable.com/platform/ai', 'M'),
        L('Superblocks', 'https://www.superblocks.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 9. 3D CREATION ──────────────────────────
    S('3D Creation', [
      S('3D models', [
        L('Meshy', 'https://www.meshy.ai/', 'T'),
        L('Luma Genie', 'https://lumalabs.ai/genie', 'T'),
        L('Tripo', 'https://www.tripo3d.ai/', 'T'),
        L('Rodin (Hyper3D)', 'https://hyper3d.ai/', 'T'),
        L('CSM.ai', 'https://csm.ai/', 'T'),
      ]),
      S('Game assets', [
        L('Scenario', 'https://www.scenario.com/', 'M'),
        L('Layer AI', 'https://layer.ai/', 'M'),
        L('Meshy', 'https://www.meshy.ai/', 'T'),
        L('Rosebud AI', 'https://rosebud.ai/', 'T'),
      ]),
      S('CAD concepts', [
        L('Autodesk Fusion AI', 'https://www.autodesk.com/products/fusion-360/', 'M'),
        L('nTop', 'https://www.ntop.com/', 'M'),
        L('SolidWorks AI', 'https://www.solidworks.com/', 'M'),
      ]),
      S('Furniture', [
        L('Kaedim (furniture)', 'https://www.kaedim3d.com/', 'M'),
        L('Alpha3D', 'https://www.alpha3d.io/', 'M'),
      ]),
      S('Product prototypes', [
        L('Vizcom', 'https://www.vizcom.ai/', 'M'),
        L('Krea Sketch', 'https://www.krea.ai/', 'M'),
        L('Autodesk Alias', 'https://www.autodesk.com/products/alias-products/', 'M'),
      ]),
      S('Sculptures', [
        L('Meshy Sculpt', 'https://www.meshy.ai/', 'T'),
        L('Sloyd', 'https://www.sloyd.ai/', 'T'),
      ]),
      S('Texture generation', [
        L('Poly.cam', 'https://poly.cam/', 'T'),
        L('Materialize', 'https://boundingboxsoftware.com/materialize/', 'T'),
        L('Substance 3D AI', 'https://www.adobe.com/products/substance3d-sampler.html', 'M'),
        L('Dream Textures (Blender)', 'https://github.com/carson-katri/dream-textures', 'T'),
      ]),
      S('Rigging', [
        L('AccuRIG (Reallusion)', 'https://actorcore.reallusion.com/auto-rig', 'T'),
        L('Mixamo (Adobe)', 'https://www.mixamo.com/', 'T'),
        L('AnythingWorld', 'https://anything.world/', 'M'),
      ]),
      S('Animation', [
        L('Cascadeur', 'https://cascadeur.com/', 'T'),
        L('DeepMotion', 'https://www.deepmotion.com/', 'M'),
        L('Move.ai', 'https://www.move.ai/', 'M'),
        L('Plask', 'https://plask.ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 10. GAME DEVELOPMENT ──────────────────────────
    S('Game Development', [
      S('Game worlds', [
        L('Skybox Blockade Labs', 'https://skybox.blockadelabs.com/', 'T'),
        L('Rosebud AI', 'https://rosebud.ai/', 'T'),
        L('Ludo.ai', 'https://ludo.ai/', 'M'),
      ]),
      S('NPC dialogue', [
        L('Inworld AI', 'https://inworld.ai/', 'M'),
        L('Convai', 'https://www.convai.com/', 'M'),
        L('Charisma.ai', 'https://charisma.ai/', 'M'),
      ]),
      S('Level design', [
        L('Promethean AI', 'https://prometheanai.com/', 'M'),
        L('Ludo.ai', 'https://ludo.ai/', 'M'),
      ]),
      S('Game code', [
        L('Rosebud AI', 'https://rosebud.ai/', 'T'),
        L('Cursor', 'https://cursor.com/', 'M'),
        L('Godot Copilot ext.', 'https://godotengine.org/', 'T'),
      ]),
      S('Pixel art', [
        L('PixelLab.ai', 'https://www.pixellab.ai/', 'M'),
        L('Scenario Pixel', 'https://www.scenario.com/', 'M'),
        L('Retro Diffusion', 'https://www.retrodiffusion.ai/', 'M'),
      ]),
      S('Soundtracks', [
        L('AIVA', 'https://www.aiva.ai/', 'T'),
        L('Loudly', 'https://www.loudly.com/', 'T'),
        L('Ecrett Music', 'https://ecrettmusic.com/', 'M'),
      ]),
      S('Quests', [
        L('Ludo.ai', 'https://ludo.ai/', 'M'),
        L('Inworld Narrative', 'https://inworld.ai/', 'M'),
        L('ChatGPT (custom GM)', 'https://chat.openai.com/', 'T'),
      ]),
      S('Maps', [
        L('Watabou generators', 'https://watabou.itch.io/', 'T'),
        L('Inkarnate', 'https://inkarnate.com/', 'M'),
        L('Dungeon Alchemist', 'https://www.dungeonalchemist.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 11. ARCHITECTURE & ENGINEERING ──────────────────────────
    S('Architecture & Engineering', [
      S('Floor plans', [
        L('Maket', 'https://www.maket.ai/', 'M'),
        L('Planner 5D AI', 'https://planner5d.com/', 'M'),
        L('ArkoAI', 'https://arko.ai/', 'M'),
      ]),
      S('Building concepts', [
        L('Veras (Evolvelab)', 'https://www.evolvelab.io/veras', 'M'),
        L('Autodesk Forma', 'https://www.autodesk.com/products/forma', 'M'),
        L('ArchitectGPT', 'https://architectgpt.io/', 'M'),
      ]),
      S('Structural concepts', [
        L('Autodesk Structural Bridge', 'https://www.autodesk.com/', 'M'),
        L('nTop', 'https://www.ntop.com/', 'M'),
      ]),
      S('Landscape design', [
        L('LookX', 'https://lookx.ai/', 'M'),
        L('Vizcom', 'https://www.vizcom.ai/', 'M'),
        L('MyLandscapes AI', 'https://mylandscapes.com/', 'M'),
      ]),
      S('Lighting layouts', [
        L('Lightical', 'https://www.lightical.com/', 'M'),
        L('Relux AI', 'https://relux.com/', 'M'),
      ]),
      S('BIM assistance', [
        L('Autodesk AI (Revit)', 'https://www.autodesk.com/', 'M'),
        L('Snaptrude', 'https://www.snaptrude.com/', 'M'),
        L('Motif', 'https://motif.io/', 'M'),
      ]),
      S('Civil engineering assistance', [
        L('Autodesk Civil 3D AI', 'https://www.autodesk.com/products/civil-3d/', 'M'),
        L('Bentley AI Assistant', 'https://www.bentley.com/', 'M'),
      ]),
      S('Mechanical design concepts', [
        L('nTop', 'https://www.ntop.com/', 'M'),
        L('PTC Creo AI', 'https://www.ptc.com/en/products/creo', 'M'),
        L('Onshape AI', 'https://www.onshape.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 12. INDUSTRIAL & MANUFACTURING ──────────────────────────
    S('Industrial & Manufacturing', [
      S('Product design', [
        L('Vizcom', 'https://www.vizcom.ai/', 'M'),
        L('Uizard Autodesigner', 'https://uizard.io/', 'M'),
        L('Kaedim', 'https://www.kaedim3d.com/', 'M'),
      ]),
      S('Packaging', [
        L('Packly AI', 'https://www.packly.com/', 'M'),
        L('Canva AI Packaging', 'https://www.canva.com/', 'M'),
        L('Booth.ai', 'https://booth.ai/', 'M'),
      ]),
      S('Manufacturing optimization', [
        L('Instrumental AI', 'https://instrumental.com/', 'M'),
        L('Augury', 'https://www.augury.com/', 'M'),
        L('Uptake', 'https://www.uptake.com/', 'M'),
      ]),
      S('CAD drafting assistance', [
        L('Autodesk Fusion AI', 'https://www.autodesk.com/products/fusion-360/', 'M'),
        L('CADchat', 'https://cadchat.com/', 'M'),
      ]),
      S('Factory simulations', [
        L('NVIDIA Omniverse Isaac', 'https://developer.nvidia.com/isaac-sim', 'R'),
        L('Siemens Plant Simulation', 'https://www.plm.automation.siemens.com/', 'M'),
        L('FlexSim', 'https://www.flexsim.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 13. FASHION ──────────────────────────
    S('Fashion', [
      S('Clothing design', [
        L('CALA', 'https://ca.la/', 'M'),
        L('Refabric', 'https://refabric.com/', 'M'),
        L('New Arc', 'https://www.newarc.ai/', 'M'),
      ]),
      S('Outfit generation', [
        L('Vue.ai', 'https://vue.ai/', 'M'),
        L('Botika', 'https://botika.io/', 'M'),
        L('YesPlz Style AI', 'https://yesplz.ai/', 'M'),
      ]),
      S('Pattern generation', [
        L('Patternedai', 'https://www.patterned.ai/', 'M'),
        L('Repper.app', 'https://repper.app/', 'M'),
        L('Vizcom', 'https://www.vizcom.ai/', 'M'),
      ]),
      S('Fabric visualization', [
        L('Off/Script', 'https://www.offscript.com/', 'T'),
        L('CLO Virtual Fashion', 'https://www.clo3d.com/', 'M'),
        L('Browzwear', 'https://browzwear.com/', 'M'),
      ]),
      S('Jewelry design', [
        L('CADjewel AI', 'https://www.cadjewel.com/', 'M'),
        L('JewelryDesignerPro', 'https://www.jewelrydesignerpro.com/', 'M'),
      ]),
      S('Shoe design', [
        L('Vizcom', 'https://www.vizcom.ai/', 'M'),
        L('Zellerfeld', 'https://zellerfeld.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 14. MARKETING ──────────────────────────
    S('Marketing', [
      S('Ad copy', [
        L('AdCreative.ai', 'https://www.adcreative.ai/', 'M'),
        L('Copy.ai', 'https://www.copy.ai/', 'M'),
        L('Persado', 'https://www.persado.com/', 'M'),
        L('Jasper', 'https://www.jasper.ai/', 'M'),
      ]),
      S('Banner ads', [
        L('AdCreative.ai', 'https://www.adcreative.ai/', 'M'),
        L('Bannerbear', 'https://www.bannerbear.com/', 'M'),
        L('Creatopy', 'https://www.creatopy.com/', 'M'),
      ]),
      S('Social media posts', [
        L('Predis.ai', 'https://predis.ai/', 'M'),
        L('Ocoya', 'https://www.ocoya.com/', 'M'),
        L('FeedHive', 'https://feedhive.com/', 'M'),
        L('Buffer AI', 'https://buffer.com/ai-assistant', 'M'),
      ]),
      S('SEO articles', [
        L('Surfer SEO', 'https://surferseo.com/', 'M'),
        L('Frase', 'https://www.frase.io/', 'M'),
        L('Clearscope', 'https://www.clearscope.io/', 'M'),
        L('Byword', 'https://byword.ai/', 'M'),
      ]),
      S('Brand strategy', [
        L('Brandmark', 'https://brandmark.io/', 'M'),
        L('Delve AI', 'https://www.delve.ai/', 'M'),
        L('Persado', 'https://www.persado.com/', 'M'),
      ]),
      S('Marketing plans', [
        L('Marketing Plan AI (HubSpot)', 'https://www.hubspot.com/artificial-intelligence', 'M'),
        L('Simplified', 'https://simplified.com/', 'M'),
      ]),
      S('Email campaigns', [
        L('Seventh Sense', 'https://www.theseventhsense.com/', 'M'),
        L('Instantly.ai', 'https://instantly.ai/', 'M'),
        L('Mailchimp Intuit AI', 'https://mailchimp.com/', 'M'),
      ]),
      S('Funnels', [
        L('Kartra AI', 'https://home.kartra.com/', 'M'),
        L('ClickFunnels AI', 'https://www.clickfunnels.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 15. SALES ──────────────────────────
    S('Sales', [
      S('Sales emails', [
        L('Lavender.ai', 'https://www.lavender.ai/', 'M'),
        L('Regie.ai', 'https://www.regie.ai/', 'M'),
        L('Instantly.ai', 'https://instantly.ai/', 'M'),
      ]),
      S('CRM automation', [
        L('Salesforce Einstein', 'https://www.salesforce.com/artificial-intelligence/', 'M'),
        L('HubSpot AI', 'https://www.hubspot.com/artificial-intelligence', 'M'),
        L('Attio AI', 'https://attio.com/', 'M'),
      ]),
      S('Lead scoring', [
        L('Clay', 'https://www.clay.com/', 'M'),
        L('Apollo.io', 'https://www.apollo.io/', 'M'),
        L('MadKudu', 'https://www.madkudu.com/', 'M'),
      ]),
      S('Cold outreach', [
        L('Apollo.io', 'https://www.apollo.io/', 'M'),
        L('Instantly.ai', 'https://instantly.ai/', 'M'),
        L('Smartlead', 'https://www.smartlead.ai/', 'M'),
      ]),
      S('Proposal writing', [
        L('PandaDoc AI', 'https://www.pandadoc.com/', 'M'),
        L('Proposify AI', 'https://www.proposify.com/', 'M'),
        L('Better Proposals', 'https://betterproposals.io/', 'M'),
      ]),
      S('Call summaries', [
        L('Gong', 'https://www.gong.io/', 'M'),
        L('Chorus (ZoomInfo)', 'https://www.zoominfo.com/products/conversation-intelligence', 'M'),
        L('Fathom', 'https://fathom.video/', 'T'),
        L('Fireflies.ai', 'https://fireflies.ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 16. BUSINESS ──────────────────────────
    S('Business', [
      S('Business plans', [
        L('Upmetrics AI', 'https://upmetrics.co/', 'M'),
        L('LivePlan', 'https://www.liveplan.com/', 'M'),
        L('15minuteplan.ai', 'https://15minuteplan.ai/', 'M'),
      ]),
      S('Financial forecasting', [
        L('Cube', 'https://www.cube.dev/', 'M'),
        L('Anaplan AI', 'https://www.anaplan.com/', 'M'),
        L('Runway.com', 'https://runway.com/', 'M'),
      ]),
      S('Pitch decks', [
        L('Gamma', 'https://gamma.app/', 'T'),
        L('Tome', 'https://tome.app/', 'T'),
        L('Beautiful.ai', 'https://www.beautiful.ai/', 'M'),
        L('Pitch AI', 'https://pitch.com/', 'M'),
      ]),
      S('SWOT analysis', [
        L('Taskade AI', 'https://www.taskade.com/', 'M'),
        L('ChatGPT', 'https://chat.openai.com/', 'T'),
        L('Miro AI', 'https://miro.com/ai/', 'M'),
      ]),
      S('Market research', [
        L('Perplexity Pro', 'https://www.perplexity.ai/', 'M'),
        L('Speak', 'https://www.speakai.co/', 'M'),
        L('Poll the People', 'https://pollthepeople.app/', 'M'),
      ]),
      S('Competitor analysis', [
        L('Kompyte', 'https://www.kompyte.com/', 'M'),
        L('Crayon', 'https://www.crayon.co/', 'M'),
        L('Klue', 'https://klue.com/', 'M'),
      ]),
      S('SOP creation', [
        L('Scribe', 'https://scribehow.com/', 'M'),
        L('Trainual AI', 'https://trainual.com/', 'M'),
        L('Guidde', 'https://www.guidde.com/', 'M'),
      ]),
      S('Workflow automation', [
        L('Zapier AI Agents', 'https://zapier.com/agents', 'M'),
        L('n8n', 'https://n8n.io/', 'T'),
        L('Make.com', 'https://www.make.com/', 'M'),
        L('Lindy.ai', 'https://www.lindy.ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 17. FINANCE ──────────────────────────
    S('Finance', [
      S('Budgeting', [
        L('Cleo', 'https://web.meetcleo.com/', 'T'),
        L('Rocket Money', 'https://www.rocketmoney.com/', 'M'),
        L('YNAB', 'https://www.ynab.com/', 'M'),
      ]),
      S('Investing research', [
        L('Kavout', 'https://www.kavout.com/', 'M'),
        L('Composer AI', 'https://www.composer.trade/', 'M'),
        L('Danelfin', 'https://danelfin.com/', 'M'),
        L('AlphaSense', 'https://www.alpha-sense.com/', 'M'),
      ]),
      S('Expense categorization', [
        L('Ramp AI', 'https://ramp.com/', 'M'),
        L('Expensify AI', 'https://www.expensify.com/', 'M'),
        L('Brex AI', 'https://www.brex.com/', 'M'),
      ]),
      S('Tax assistance', [
        L('TurboTax AI', 'https://turbotax.intuit.com/', 'M'),
        L('Keeper Tax', 'https://www.keepertax.com/', 'M'),
        L('April', 'https://www.getapril.com/', 'M'),
      ]),
      S('Forecasting', [
        L('Anaplan AI', 'https://www.anaplan.com/', 'M'),
        L('Vena AI', 'https://www.venasolutions.com/', 'M'),
        L('Cube', 'https://www.cube.dev/', 'M'),
      ]),
      S('Risk analysis', [
        L('Riskonnect AI', 'https://riskonnect.com/', 'M'),
        L('Kensho', 'https://kensho.com/', 'M'),
        L('DataRobot', 'https://www.datarobot.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 18. EDUCATION ──────────────────────────
    S('Education', [
      S('Tutors', [
        L('Khanmigo (Khan Academy)', 'https://www.khanmigo.ai/', 'M'),
        L('Duolingo Max', 'https://www.duolingo.com/', 'M'),
        L('Socratic (Google)', 'https://socratic.org/', 'T'),
        L('Photomath', 'https://photomath.com/', 'T'),
      ]),
      S('Quiz generation', [
        L('Quizgecko', 'https://quizgecko.com/', 'M'),
        L('Yippity', 'https://yippity.io/', 'M'),
        L('Conker', 'https://www.conker.ai/', 'T'),
        L('QuestionWell', 'https://questionwell.org/', 'T'),
      ]),
      S('Flashcards', [
        L('Anki + ChatGPT', 'https://apps.ankiweb.net/', 'T'),
        L('Quizlet Magic Notes', 'https://quizlet.com/', 'M'),
        L('Wisdolia', 'https://www.wisdolia.com/', 'M'),
      ]),
      S('Lesson plans', [
        L('Curipod', 'https://curipod.com/', 'M'),
        L('MagicSchool.ai', 'https://www.magicschool.ai/', 'M'),
        L('Eduaide.ai', 'https://www.eduaide.ai/', 'M'),
        L('Diffit', 'https://beta.diffit.me/', 'T'),
      ]),
      S('Homework help', [
        L('Photomath', 'https://photomath.com/', 'T'),
        L('Wolfram Alpha', 'https://www.wolframalpha.com/', 'T'),
        L('Caktus AI', 'https://caktus.ai/', 'D'),
        L('StudyX', 'https://studyx.ai/', 'D'),
      ]),
      S('Language learning', [
        L('Speak', 'https://www.speak.com/', 'M'),
        L('Duolingo', 'https://www.duolingo.com/', 'T'),
        L('LangoTalk', 'https://langotalk.ai/', 'M'),
        L('TalkPal', 'https://talkpal.ai/', 'M'),
      ]),
      S('Course creation', [
        L('Kajabi AI', 'https://kajabi.com/', 'M'),
        L('Coursebox AI', 'https://www.coursebox.ai/', 'M'),
        L('Mini Course Generator', 'https://www.minicoursegenerator.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 19. HEALTHCARE ──────────────────────────
    S('Healthcare', [
      S('Medical transcription', [
        L('Nuance DAX Copilot', 'https://www.nuance.com/healthcare/dragon-ai-clinical-solutions/dax-copilot.html', 'M'),
        L('Abridge', 'https://www.abridge.com/', 'M'),
        L('DeepScribe', 'https://www.deepscribe.ai/', 'M'),
      ]),
      S('Clinical documentation', [
        L('Suki AI', 'https://www.suki.ai/', 'M'),
        L('Ambience Healthcare', 'https://www.ambiencehealthcare.com/', 'M'),
        L('Augmedix', 'https://www.augmedix.com/', 'M'),
      ]),
      S('Medical imaging assistance', [
        L('Aidoc', 'https://www.aidoc.com/', 'M'),
        L('Zebra Medical', 'https://www.zebra-med.com/', 'M'),
        L('Butterfly iQ+ AI', 'https://www.butterflynetwork.com/', 'M'),
      ]),
      S('Drug discovery', [
        L('AlphaFold 3', 'https://alphafoldserver.com/', 'R'),
        L('Insilico Medicine', 'https://insilico.com/', 'M'),
        L('Atomwise', 'https://www.atomwise.com/', 'M'),
      ]),
      S('Patient education', [
        L('Klara', 'https://www.klara.com/', 'M'),
        L('Ada Health', 'https://ada.com/', 'T'),
      ]),
      S('Symptom guidance (not diagnosis)', [
        L('Ada Health', 'https://ada.com/', 'T'),
        L('Buoy Health', 'https://www.buoyhealth.com/', 'T'),
        L('Isabel', 'https://symptomchecker.isabelhealthcare.com/', 'T'),
      ]),
    ]),

    // ────────────────────────── 20. LEGAL ──────────────────────────
    S('Legal', [
      S('Contract review', [
        L('LawGeex', 'https://www.lawgeex.com/', 'M'),
        L('Spellbook', 'https://www.spellbook.legal/', 'M'),
        L('Ironclad AI', 'https://ironcladapp.com/', 'M'),
      ]),
      S('Legal research', [
        L('Harvey AI', 'https://www.harvey.ai/', 'M'),
        L('Lex Machina', 'https://lexmachina.com/', 'M'),
        L('Casetext CoCounsel', 'https://casetext.com/', 'M'),
      ]),
      S('Document drafting', [
        L('Robin AI', 'https://www.robinai.com/', 'M'),
        L('Genie AI', 'https://www.genieai.co/', 'M'),
        L('DoNotPay', 'https://donotpay.com/', 'M'),
      ]),
      S('Compliance assistance', [
        L('Vanta AI', 'https://www.vanta.com/', 'M'),
        L('Drata', 'https://drata.com/', 'M'),
        L('Norm Ai', 'https://www.normai.com/', 'M'),
      ]),
      S('Policy generation', [
        L('Termly', 'https://termly.io/', 'M'),
        L('iubenda AI', 'https://www.iubenda.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 21. SCIENCE & RESEARCH ──────────────────────────
    S('Science & Research', [
      S('Literature reviews', [
        L('Elicit', 'https://elicit.com/', 'T'),
        L('SciSpace', 'https://scispace.com/', 'T'),
        L('ResearchRabbit', 'https://www.researchrabbit.ai/', 'T'),
        L('Undermind', 'https://www.undermind.ai/', 'M'),
      ]),
      S('Data analysis', [
        L('Julius AI', 'https://julius.ai/', 'M'),
        L('Code Interpreter (ChatGPT)', 'https://chat.openai.com/', 'M'),
        L('Deepnote AI', 'https://deepnote.com/ai', 'M'),
      ]),
      S('Citation assistance', [
        L('Scite.ai', 'https://scite.ai/', 'M'),
        L('Zotero + GPT', 'https://www.zotero.org/', 'T'),
        L('EndNote AI', 'https://endnote.com/', 'M'),
      ]),
      S('Experiment design', [
        L('Elicit', 'https://elicit.com/', 'T'),
        L('Consensus', 'https://consensus.app/', 'T'),
      ]),
      S('Hypothesis generation', [
        L('SciSpace Copilot', 'https://scispace.com/', 'T'),
        L('Semantic Scholar TLDR', 'https://www.semanticscholar.org/', 'T'),
      ]),
    ]),

    // ────────────────────────── 22. DATA & ANALYTICS ──────────────────────────
    S('Data & Analytics', [
      S('Spreadsheet analysis', [
        L('SheetAI', 'https://sheetai.app/', 'M'),
        L('Rows AI', 'https://rows.com/', 'T'),
        L('Formula Bot', 'https://formulabot.com/', 'M'),
      ]),
      S('Charts', [
        L('ChartGPT', 'https://www.chartgpt.dev/', 'T'),
        L('Rath by Kanaries', 'https://kanaries.net/', 'T'),
        L('Datylon AI', 'https://www.datylon.com/', 'M'),
      ]),
      S('Dashboards', [
        L('Rows AI', 'https://rows.com/', 'T'),
        L('Basedash', 'https://www.basedash.com/', 'M'),
        L('Tableau Pulse', 'https://www.tableau.com/products/tableau-pulse', 'M'),
      ]),
      S('Business intelligence', [
        L('ThoughtSpot Sage', 'https://www.thoughtspot.com/sage', 'M'),
        L('Sisense AI', 'https://www.sisense.com/', 'M'),
        L('Power BI Copilot', 'https://powerbi.microsoft.com/', 'M'),
      ]),
      S('Predictive analytics', [
        L('DataRobot', 'https://www.datarobot.com/', 'M'),
        L('H2O.ai', 'https://h2o.ai/', 'M'),
        L('Dataiku', 'https://www.dataiku.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 23. ROBOTICS ──────────────────────────
    S('Robotics', [
      S('Robot programming', [
        L('NVIDIA Isaac', 'https://developer.nvidia.com/isaac', 'R'),
        L('Wandelbots', 'https://wandelbots.com/', 'M'),
        L('Rapid Robotics', 'https://www.rapidrobotics.com/', 'M'),
      ]),
      S('Path planning', [
        L('OMPL', 'https://ompl.kavrakilab.org/', 'T'),
        L('Realtime Robotics', 'https://rtr.ai/', 'M'),
        L('MoveIt', 'https://moveit.ai/', 'T'),
      ]),
      S('Vision systems', [
        L('Roboflow', 'https://roboflow.com/', 'T'),
        L('Landing AI', 'https://landing.ai/', 'M'),
        L('Cognex ViDi', 'https://www.cognex.com/', 'M'),
      ]),
      S('Automation workflows', [
        L('UiPath AI', 'https://www.uipath.com/', 'M'),
        L('Automation Anywhere', 'https://www.automationanywhere.com/', 'M'),
        L('Blue Prism', 'https://www.blueprism.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 24. PRODUCTIVITY ──────────────────────────
    S('Productivity', [
      S('AI assistants', [
        L('ChatGPT', 'https://chat.openai.com/', 'T'),
        L('Claude', 'https://claude.ai/', 'T'),
        L('Gemini', 'https://gemini.google.com/', 'T'),
        L('Microsoft Copilot', 'https://copilot.microsoft.com/', 'T'),
        L('Pi', 'https://pi.ai/', 'T'),
      ]),
      S('Scheduling', [
        L('Reclaim.ai', 'https://reclaim.ai/', 'M'),
        L('Motion', 'https://www.usemotion.com/', 'M'),
        L('Clockwise', 'https://www.getclockwise.com/', 'M'),
        L('Clara', 'https://claralabs.com/', 'M'),
      ]),
      S('Meeting summaries', [
        L('Otter.ai', 'https://otter.ai/', 'M'),
        L('Fireflies.ai', 'https://fireflies.ai/', 'M'),
        L('tl;dv', 'https://tldv.io/', 'M'),
        L('Fathom', 'https://fathom.video/', 'T'),
        L('Granola', 'https://www.granola.ai/', 'M'),
      ]),
      S('Task management', [
        L('Motion', 'https://www.usemotion.com/', 'M'),
        L('Taskade AI', 'https://www.taskade.com/', 'M'),
        L('Todoist AI', 'https://todoist.com/', 'M'),
        L('ClickUp AI', 'https://clickup.com/ai', 'M'),
      ]),
      S('Note organization', [
        L('Notion AI', 'https://www.notion.so/product/ai', 'M'),
        L('Mem.ai', 'https://get.mem.ai/', 'M'),
        L('Reflect', 'https://reflect.app/', 'M'),
        L('Google NotebookLM', 'https://notebooklm.google.com/', 'T'),
      ]),
    ]),

    // ────────────────────────── 25. CUSTOMER SERVICE ──────────────────────────
    S('Customer Service', [
      S('AI chatbots', [
        L('Intercom Fin', 'https://www.intercom.com/fin', 'M'),
        L('Ada', 'https://www.ada.cx/', 'M'),
        L('Drift', 'https://www.drift.com/', 'M'),
        L('Zendesk AI', 'https://www.zendesk.com/service/ai/', 'M'),
      ]),
      S('Help desk automation', [
        L('Forethought', 'https://forethought.ai/', 'M'),
        L('Kustomer AI', 'https://www.kustomer.com/', 'M'),
        L('Freshdesk Freddy', 'https://www.freshworks.com/freshdesk/', 'M'),
      ]),
      S('FAQ generation', [
        L('DocsBot AI', 'https://docsbot.ai/', 'M'),
        L('MyAskAI', 'https://myaskai.com/', 'M'),
      ]),
      S('Ticket routing', [
        L('Zendesk AI', 'https://www.zendesk.com/service/ai/', 'M'),
        L('Salesforce Service Cloud AI', 'https://www.salesforce.com/service/', 'M'),
      ]),
    ]),

    // ────────────────────────── 26. DESIGN ──────────────────────────
    S('Design', [
      S('UI / UX design', [
        L('Uizard', 'https://uizard.io/', 'M'),
        L('Galileo AI', 'https://www.usegalileo.ai/', 'M'),
        L('Musho', 'https://musho.ai/', 'M'),
      ]),
      S('Wireframes', [
        L('Uizard Wireframe', 'https://uizard.io/wireframing/', 'M'),
        L('Visily', 'https://www.visily.ai/', 'M'),
        L('Whimsical AI', 'https://whimsical.com/ai', 'M'),
      ]),
      S('Presentations', [
        L('Gamma', 'https://gamma.app/', 'T'),
        L('Tome', 'https://tome.app/', 'T'),
        L('Decktopus', 'https://www.decktopus.com/', 'M'),
        L('Beautiful.ai', 'https://www.beautiful.ai/', 'M'),
      ]),
      S('Brand identity', [
        L('Looka', 'https://looka.com/', 'M'),
        L('Brandmark', 'https://brandmark.io/', 'M'),
        L('Zebra Brand', 'https://withzebra.com/', 'M'),
      ]),
      S('Slide decks', [
        L('SlidesAI', 'https://www.slidesai.io/', 'M'),
        L('Plus AI (Google Slides)', 'https://www.plusdocs.com/plus-ai-for-google-slides', 'M'),
        L('Presentations.AI', 'https://www.presentations.ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 27. SOCIAL MEDIA ──────────────────────────
    S('Social Media', [
      S('Caption generation', [
        L('Copy.ai Social', 'https://www.copy.ai/', 'M'),
        L('Predis.ai', 'https://predis.ai/', 'M'),
        L('Simplified Captions', 'https://simplified.com/', 'M'),
      ]),
      S('Thumbnail creation', [
        L('Canva AI', 'https://www.canva.com/', 'T'),
        L('Thumbnail.AI', 'https://thumbnail.ai/', 'M'),
        L('Fotor Thumbnail Maker', 'https://www.fotor.com/features/youtube-thumbnail-maker/', 'M'),
      ]),
      S('Content calendars', [
        L('FeedHive', 'https://feedhive.com/', 'M'),
        L('Publer AI', 'https://publer.io/', 'M'),
        L('Loomly', 'https://www.loomly.com/', 'M'),
      ]),
      S('Trend analysis', [
        L('Exploding Topics', 'https://explodingtopics.com/', 'M'),
        L('BuzzSumo AI', 'https://buzzsumo.com/', 'M'),
        L('Trendspider', 'https://trendspider.com/', 'M'),
      ]),
      S('Viral hooks', [
        L('Hootsuite OwlyWriter', 'https://www.hootsuite.com/platform/owlywriter-ai', 'M'),
        L('Hook Line', 'https://hookline.app/', 'M'),
        L('TweetHunter AI', 'https://tweethunter.io/', 'M'),
      ]),
      S('Influencer content', [
        L('Taplio (LinkedIn)', 'https://taplio.com/', 'M'),
        L('TweetHunter', 'https://tweethunter.io/', 'M'),
        L('Metricool AI', 'https://metricool.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 28. VIRTUAL HUMANS ──────────────────────────
    S('Virtual Humans', [
      S('AI influencers', [
        L('Aitana Lopez (case study)', 'https://www.euronews.com/next/2023/12/03/how-spains-first-ai-influencer-aitana-lopez-is-making-almost-11000-a-month', 'T'),
        L('Lil Miquela', 'https://www.instagram.com/lilmiquela/', 'T'),
        L('CreatorAI', 'https://creatorai.com/', 'M'),
      ]),
      S('AI companions', [
        L('Replika', 'https://replika.com/', 'M'),
        L('Character.AI', 'https://character.ai/', 'T'),
        L('Pi', 'https://pi.ai/', 'T'),
        L('Nomi.ai', 'https://nomi.ai/', 'D'),
      ]),
      S('Digital employees', [
        L('Sana AI', 'https://sanalabs.com/', 'M'),
        L('11x AI (Alice/Julian)', 'https://www.11x.ai/', 'M'),
        L('Devin (SWE)', 'https://www.cognition.ai/blog/introducing-devin', 'R'),
      ]),
      S('Customer avatars', [
        L('Uniphore', 'https://www.uniphore.com/', 'M'),
        L('Soul Machines', 'https://www.soulmachines.com/', 'M'),
      ]),
      S('Virtual presenters', [
        L('HeyGen', 'https://www.heygen.com/', 'M'),
        L('Synthesia', 'https://www.synthesia.io/', 'M'),
        L('Colossyan', 'https://www.colossyan.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 29. PHOTOGRAPHY ──────────────────────────
    S('Photography', [
      S('Background removal', [
        L('Remove.bg', 'https://www.remove.bg/', 'T'),
        L('Photoroom', 'https://www.photoroom.com/', 'T'),
        L('BRIA', 'https://bria.ai/', 'M'),
      ]),
      S('Image enhancement', [
        L('Topaz Photo AI', 'https://www.topazlabs.com/topaz-photo-ai', 'M'),
        L('Let\u2019s Enhance', 'https://letsenhance.io/', 'M'),
        L('Vance AI', 'https://vanceai.com/', 'M'),
      ]),
      S('Face restoration', [
        L('GFPGAN', 'https://github.com/TencentARC/GFPGAN', 'T'),
        L('CodeFormer', 'https://shangchenzhou.com/projects/CodeFormer/', 'T'),
        L('Remini', 'https://remini.ai/', 'M'),
      ]),
      S('Object removal', [
        L('Cleanup.pictures', 'https://cleanup.pictures/', 'T'),
        L('Photoshop Generative Fill', 'https://www.adobe.com/products/photoshop/generative-fill.html', 'M'),
        L('Snapedit', 'https://snapedit.app/', 'M'),
      ]),
      S('Color grading', [
        L('Colourlab.ai', 'https://colourlab.ai/', 'M'),
        L('Pixels.com AI Grader', 'https://neural.love/', 'M'),
      ]),
      S('Upscaling', [
        L('Magnific', 'https://magnific.ai/', 'M'),
        L('Topaz Gigapixel AI', 'https://www.topazlabs.com/gigapixel-ai', 'M'),
        L('Upscayl (open source)', 'https://www.upscayl.org/', 'T'),
      ]),
    ]),

    // ────────────────────────── 30. DOCUMENT PROCESSING ──────────────────────────
    S('Document Processing', [
      S('OCR', [
        L('Google Cloud Vision', 'https://cloud.google.com/vision', 'R'),
        L('Amazon Textract', 'https://aws.amazon.com/textract/', 'R'),
        L('Mathpix', 'https://mathpix.com/', 'M'),
      ]),
      S('PDF extraction', [
        L('ChatPDF', 'https://www.chatpdf.com/', 'T'),
        L('PDF.ai', 'https://pdf.ai/', 'M'),
        L('AskYourPDF', 'https://askyourpdf.com/', 'T'),
        L('Unstructured', 'https://unstructured.io/', 'T'),
      ]),
      S('Form filling', [
        L('Docsumo', 'https://docsumo.com/', 'M'),
        L('Docparser', 'https://docparser.com/', 'M'),
        L('Rossum', 'https://rossum.ai/', 'M'),
      ]),
      S('Invoice processing', [
        L('Rossum', 'https://rossum.ai/', 'M'),
        L('AutoEntry', 'https://www.autoentry.com/', 'M'),
        L('Nanonets', 'https://nanonets.com/', 'M'),
      ]),
      S('Receipt scanning', [
        L('Expensify AI', 'https://www.expensify.com/', 'M'),
        L('Veryfi', 'https://www.veryfi.com/', 'M'),
        L('Zoho Expense', 'https://www.zoho.com/expense/', 'M'),
      ]),
    ]),

    // ────────────────────────── 31. SECURITY ──────────────────────────
    S('Security', [
      S('Threat detection', [
        L('CrowdStrike Charlotte', 'https://www.crowdstrike.com/', 'M'),
        L('SentinelOne Purple AI', 'https://www.sentinelone.com/platform/purple-ai/', 'M'),
        L('Darktrace', 'https://darktrace.com/', 'M'),
      ]),
      S('Malware analysis', [
        L('VirusTotal', 'https://www.virustotal.com/', 'T'),
        L('Hybrid Analysis', 'https://www.hybrid-analysis.com/', 'T'),
        L('MalwareBazaar', 'https://bazaar.abuse.ch/', 'T'),
      ]),
      S('Fraud detection', [
        L('Sift', 'https://sift.com/', 'M'),
        L('Sardine', 'https://sardine.ai/', 'M'),
        L('Feedzai', 'https://feedzai.com/', 'M'),
      ]),
      S('Identity verification', [
        L('Persona', 'https://withpersona.com/', 'M'),
        L('Onfido', 'https://onfido.com/', 'M'),
        L('Jumio', 'https://www.jumio.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 32. AUTOMATION & AGENTS ──────────────────────────
    S('Automation & Agents', [
      S('Personal AI agents', [
        L('Manus AI', 'https://manus.im/', 'R'),
        L('Multi-On', 'https://www.multion.ai/', 'R'),
        L('Rabbit R1', 'https://www.rabbit.tech/', 'M'),
        L('OpenAI Operator', 'https://openai.com/index/introducing-operator/', 'M'),
      ]),
      S('Business workflow agents', [
        L('Zapier AI Agents', 'https://zapier.com/agents', 'M'),
        L('Lindy.ai', 'https://www.lindy.ai/', 'M'),
        L('Relay.app', 'https://www.relay.app/', 'M'),
        L('n8n', 'https://n8n.io/', 'T'),
      ]),
      S('Multi-agent systems', [
        L('CrewAI', 'https://www.crewai.com/', 'T'),
        L('AutoGen (Microsoft)', 'https://microsoft.github.io/autogen/', 'T'),
        L('LangGraph', 'https://www.langchain.com/langgraph', 'T'),
        L('Swarm (OpenAI)', 'https://github.com/openai/swarm', 'T'),
      ]),
      S('Browser automation', [
        L('Browser Use', 'https://browser-use.com/', 'T'),
        L('Skyvern', 'https://www.skyvern.com/', 'T'),
        L('Nanobrowser', 'https://nanobrowser.ai/', 'T'),
        L('Reworkd', 'https://reworkd.ai/', 'T'),
      ]),
      S('Email automation', [
        L('Superhuman AI', 'https://superhuman.com/ai', 'M'),
        L('Shortwave', 'https://www.shortwave.com/', 'M'),
        L('SaneBox', 'https://www.sanebox.com/', 'M'),
      ]),
      S('Computer-use agents', [
        L('Claude Computer Use', 'https://www.anthropic.com/news/3-5-models-and-computer-use', 'R'),
        L('OpenAI Operator', 'https://openai.com/index/introducing-operator/', 'M'),
        L('OS-World benchmark', 'https://os-world.github.io/', 'T'),
      ]),
    ]),

    // ────────────────────────── 33. SIMULATION ──────────────────────────
    S('Simulation', [
      S('Physics simulations', [
        L('NVIDIA Omniverse', 'https://www.nvidia.com/en-us/omniverse/', 'R'),
        L('MuJoCo', 'https://mujoco.org/', 'T'),
        L('PyBullet', 'https://pybullet.org/', 'T'),
      ]),
      S('Traffic simulations', [
        L('PTV Vissim', 'https://www.ptvgroup.com/en/products/ptv-vissim/', 'M'),
        L('SUMO', 'https://www.eclipse.org/sumo/', 'T'),
        L('Aimsun Next', 'https://www.aimsun.com/', 'M'),
      ]),
      S('Financial simulations', [
        L('Palantir Foundry', 'https://www.palantir.com/platforms/foundry/', 'M'),
        L('AnyLogic', 'https://www.anylogic.com/', 'M'),
      ]),
      S('Digital twins', [
        L('NVIDIA Omniverse', 'https://www.nvidia.com/en-us/omniverse/', 'R'),
        L('Siemens Xcelerator', 'https://www.siemens.com/', 'M'),
        L('Azure Digital Twins', 'https://azure.microsoft.com/en-us/products/digital-twins/', 'R'),
      ]),
    ]),

    // ────────────────────────── 34. GEOGRAPHIC & MAPPING ──────────────────────────
    S('Geographic & Mapping', [
      S('GIS analysis', [
        L('ArcGIS AI', 'https://www.esri.com/en-us/arcgis/ai-in-gis/', 'M'),
        L('QGIS + plugins', 'https://qgis.org/', 'T'),
        L('Kepler.gl', 'https://kepler.gl/', 'T'),
      ]),
      S('Map generation', [
        L('Mapbox AI', 'https://www.mapbox.com/', 'M'),
        L('Blackshark.ai', 'https://blackshark.ai/', 'M'),
        L('Overture Maps', 'https://overturemaps.org/', 'T'),
      ]),
      S('Satellite imagery analysis', [
        L('Planet Labs AI', 'https://www.planet.com/', 'M'),
        L('Descartes Labs', 'https://www.descarteslabs.com/', 'M'),
        L('Orbital Insight', 'https://orbitalinsight.com/', 'M'),
      ]),
      S('Route optimization', [
        L('Routific', 'https://routific.com/', 'M'),
        L('OptimoRoute', 'https://optimoroute.com/', 'M'),
        L('Onfleet', 'https://onfleet.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 35. BIOLOGY & CHEMISTRY ──────────────────────────
    S('Biology & Chemistry', [
      S('Protein folding', [
        L('AlphaFold 3', 'https://alphafoldserver.com/', 'R'),
        L('ESMFold (Meta)', 'https://esmatlas.com/', 'T'),
        L('RoseTTAFold', 'https://github.com/RosettaCommons/RoseTTAFold', 'T'),
      ]),
      S('Molecule generation', [
        L('Insilico Chemistry42', 'https://insilico.com/chemistry42', 'M'),
        L('BenevolentAI', 'https://www.benevolent.com/', 'M'),
        L('Iktos', 'https://iktos.ai/', 'M'),
      ]),
      S('Chemical simulations', [
        L('Schrodinger', 'https://www.schrodinger.com/', 'M'),
        L('MolPAL', 'https://github.com/coleygroup/molpal', 'T'),
      ]),
      S('Genetic analysis', [
        L('DeepVariant', 'https://github.com/google/deepvariant', 'T'),
        L('Enformer', 'https://github.com/deepmind/deepmind-research/tree/master/enformer', 'T'),
        L('Illumina DRAGEN AI', 'https://www.illumina.com/products/by-type/informatics-products/dragen-bio-it-platform.html', 'M'),
      ]),
    ]),

    // ────────────────────────── 36. REAL ESTATE ──────────────────────────
    S('Real Estate', [
      S('Property valuation', [
        L('HouseCanary', 'https://www.housecanary.com/', 'M'),
        L('Zillow Zestimate', 'https://www.zillow.com/z/zestimate/', 'T'),
        L('Redfin Estimate', 'https://www.redfin.com/redfin-estimate', 'T'),
      ]),
      S('Virtual staging', [
        L('VirtualStagingAI', 'https://www.virtualstagingai.app/', 'M'),
        L('REimagineHome', 'https://www.reimaginehome.ai/', 'M'),
        L('Styldod', 'https://www.styldod.com/', 'M'),
      ]),
      S('Renovation visualization', [
        L('REimagineHome', 'https://www.reimaginehome.ai/', 'M'),
        L('Interior AI', 'https://interiorai.com/', 'M'),
        L('Collov', 'https://collov.ai/', 'M'),
      ]),
      S('Investment analysis', [
        L('AlphaFlow', 'https://alphaflow.com/', 'M'),
        L('PropertyLens', 'https://propertylens.com/', 'M'),
        L('Enodo', 'https://www.enodoinc.com/', 'M'),
      ]),
      S('Listing descriptions', [
        L('ListingCopy.ai', 'https://listingcopy.ai/', 'M'),
        L('Rechat', 'https://rechat.com/', 'M'),
        L('WriteListing.com', 'https://writelisting.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 37. CREATIVE MEDIA ──────────────────────────
    S('Creative Media', [
      S('Comic books', [
        L('AI Comic Factory', 'https://aicomicfactory.app/', 'T'),
        L('Comicgen', 'https://www.comicgen.ai/', 'T'),
        L('Dashtoon', 'https://dashtoon.com/', 'T'),
      ]),
      S('Manga', [
        L('MangaGPT', 'https://www.mangagpt.com/', 'M'),
        L('Anifusion', 'https://anifusion.ai/', 'M'),
        L('Dashtoon Manga', 'https://dashtoon.com/', 'T'),
      ]),
      S('Graphic novels', [
        L('AI Comic Factory', 'https://aicomicfactory.app/', 'T'),
        L('Storyboard That', 'https://www.storyboardthat.com/', 'M'),
      ]),
      S('Storyboards', [
        L('Boords', 'https://boords.com/', 'M'),
        L('Storyboarder', 'https://wonderunit.com/storyboarder/', 'T'),
        L('LTX Studio', 'https://ltx.studio/', 'R'),
      ]),
      S('Film pre-visualization', [
        L('LTX Studio', 'https://ltx.studio/', 'R'),
        L('Filmustage', 'https://filmustage.com/', 'M'),
        L('ShotDeck AI', 'https://shotdeck.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 38. VIRTUAL REALITY & AR ──────────────────────────
    S('Virtual Reality & AR', [
      S('VR environments', [
        L('Skybox Blockade Labs', 'https://skybox.blockadelabs.com/', 'T'),
        L('WorldLabs (Fei-Fei Li)', 'https://www.worldlabs.ai/', 'R'),
        L('Meshy', 'https://www.meshy.ai/', 'T'),
      ]),
      S('AR objects', [
        L('Meta Spark AR', 'https://spark.meta.com/', 'T'),
        L('Snap Lens Studio AI', 'https://ar.snap.com/lens-studio', 'T'),
        L('8th Wall', 'https://www.8thwall.com/', 'M'),
      ]),
      S('Interactive experiences', [
        L('Inworld AI', 'https://inworld.ai/', 'M'),
        L('Convai', 'https://www.convai.com/', 'M'),
      ]),
      S('Digital twins', [
        L('NVIDIA Omniverse', 'https://www.nvidia.com/en-us/omniverse/', 'R'),
        L('Matterport AI', 'https://matterport.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 39. AI FOR PHYSICAL PRODUCTS ──────────────────────────
    S('AI for Physical Products', [
      S('Furniture', [
        L('Kaedim', 'https://www.kaedim3d.com/', 'M'),
        L('Alpha3D', 'https://www.alpha3d.io/', 'M'),
        L('Vizcom', 'https://www.vizcom.ai/', 'M'),
      ]),
      S('Toys', [
        L('Vizcom', 'https://www.vizcom.ai/', 'M'),
        L('Meshy', 'https://www.meshy.ai/', 'T'),
      ]),
      S('Electronics concepts', [
        L('Autodesk Fusion AI', 'https://www.autodesk.com/products/fusion-360/', 'M'),
        L('Zoo.dev', 'https://zoo.dev/', 'T'),
      ]),
      S('Consumer products', [
        L('Vizcom', 'https://www.vizcom.ai/', 'M'),
        L('Uizard', 'https://uizard.io/', 'M'),
      ]),
      S('Home layouts', [
        L('Planner 5D AI', 'https://planner5d.com/', 'M'),
        L('HomeStyler AI', 'https://www.homestyler.com/', 'M'),
        L('Foyr Neo', 'https://foyr.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 40. SPECIALIZED GENERATORS ──────────────────────────
    S('Specialized Generators', [
      S('Meme generators', [
        L('Supermeme.ai', 'https://www.supermeme.ai/', 'M'),
        L('imgflip AI', 'https://imgflip.com/ai-meme', 'T'),
        L('Memy.ai', 'https://memy.ai/', 'T'),
      ]),
      S('Emoji generators', [
        L('Emojis.sh (fal)', 'https://emojis.sh/', 'T'),
        L('OpenMoji AI', 'https://openmoji.org/', 'T'),
        L('Emojipedia AI', 'https://emojipedia.org/', 'T'),
      ]),
      S('Sticker generators', [
        L('Sticker Baker', 'https://stickerbaker.com/', 'T'),
        L('StickerMaker.ai', 'https://stickermaker.ai/', 'M'),
        L('Fotor Stickers', 'https://www.fotor.com/features/sticker-maker/', 'M'),
      ]),
      S('QR code art', [
        L('QRcode-monster (SD)', 'https://qrcodemonster.ai/', 'M'),
        L('QR Code AI Art', 'https://qrcodeaiart.com/', 'M'),
        L('Bitly QR AI', 'https://bitly.com/pages/landing/ai-qr-codes', 'M'),
      ]),
      S('Maps', [
        L('Inkarnate', 'https://inkarnate.com/', 'M'),
        L('Watabou generators', 'https://watabou.itch.io/', 'T'),
      ]),
      S('Family trees', [
        L('MyHeritage AI', 'https://www.myheritage.com/', 'M'),
        L('Ancestry AI', 'https://www.ancestry.com/', 'M'),
      ]),
      S('Timelines', [
        L('Timeline JS', 'https://timeline.knightlab.com/', 'T'),
        L('Preceden', 'https://www.preceden.com/', 'M'),
      ]),
      S('Mind maps', [
        L('Whimsical AI', 'https://whimsical.com/ai', 'M'),
        L('Xmind AI', 'https://xmind.ai/', 'M'),
        L('MindMeister AI', 'https://www.mindmeister.com/', 'M'),
      ]),
      S('Flowcharts', [
        L('Lucid AI', 'https://lucid.co/product/lucidchart-ai', 'M'),
        L('Whimsical Flowchart AI', 'https://whimsical.com/ai', 'M'),
        L('Miro AI', 'https://miro.com/ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 41. AI CHATBOTS / LLMs (kept from v2) ──────────────────────────
    S('AI Chatbots / LLMs', [
      S('Mainstream Assistants', [
        L('ChatGPT (GPT-4o / GPT-4.5)', 'https://chat.openai.com/', 'T'),
        L('ChatGPT o1', 'https://openai.com/o1/', 'M'),
        L('ChatGPT o3 / o3-mini', 'https://openai.com/index/openai-o3-mini/', 'M'),
        L('Claude 3.7 Sonnet', 'https://claude.ai/', 'T'),
        L('Claude 3.5 Haiku', 'https://claude.ai/', 'T'),
        L('Claude Opus', 'https://www.anthropic.com/claude', 'M'),
        L('Google Gemini 2.5 Pro', 'https://gemini.google.com/', 'T'),
        L('Gemini 2.5 Flash', 'https://gemini.google.com/', 'T'),
        L('Microsoft Copilot', 'https://copilot.microsoft.com/', 'T'),
        L('Grok 3 (xAI)', 'https://grok.x.ai/', 'R'),
        L('Perplexity', 'https://www.perplexity.ai/', 'T'),
        L('DeepSeek R1', 'https://chat.deepseek.com/', 'T'),
        L('DeepSeek V3', 'https://chat.deepseek.com/', 'T'),
        L('Mistral Le Chat', 'https://chat.mistral.ai/', 'T'),
        L('Qwen 2.5 Chat', 'https://chat.qwen.ai/', 'T'),
        L('Meta AI (Llama 3.3)', 'https://www.meta.ai/', 'T'),
        L('HuggingChat', 'https://huggingface.co/chat/', 'T'),
        L('Poe (multi-model)', 'https://poe.com/', 'T'),
        L('You.com', 'https://you.com/', 'T'),
        L('Pi (Inflection)', 'https://pi.ai/', 'T'),
        L('Kimi (Moonshot)', 'https://kimi.moonshot.cn/', 'T'),
      ]),
      S('Open Source / Local LLMs', [
        L('Ollama', 'https://ollama.com/', 'T'),
        L('LM Studio', 'https://lmstudio.ai/', 'T'),
        L('Jan.ai', 'https://jan.ai/', 'T'),
        L('GPT4All', 'https://gpt4all.io/', 'T'),
        L('Text Generation WebUI', 'https://github.com/oobabooga/text-generation-webui', 'T'),
        L('KoboldCPP', 'https://github.com/LostRuins/koboldcpp', 'T'),
        L('llama.cpp', 'https://github.com/ggerganov/llama.cpp', 'T'),
        L('vLLM', 'https://github.com/vllm-project/vllm', 'T'),
        L('Msty', 'https://msty.app/', 'T'),
      ]),
      S('Uncensored / Roleplay Chats', [
        L('Character.AI', 'https://character.ai/', 'T'),
        L('Janitor AI', 'https://janitorai.com/', 'D'),
        L('Chub.ai', 'https://chub.ai/', 'D'),
        L('SpicyChat', 'https://spicychat.ai/', 'D'),
        L('CrushOn AI', 'https://crushon.ai/', 'D'),
        L('Venus Chub', 'https://venus.chub.ai/', 'D'),
        L('SillyTavern (frontend)', 'https://sillytavernai.com/', 'T'),
        L('NovelAI', 'https://novelai.net/', 'M'),
        L('Dolphin Mistral (uncensored)', 'https://huggingface.co/cognitivecomputations', 'D'),
      ]),
      S('Specialized / Gritty GPTs', [
        L('WormGPT (Wikipedia)', 'https://en.wikipedia.org/wiki/WormGPT', 'D'),
        L('FraudGPT (news)', 'https://www.bleepingcomputer.com/news/security/new-fraudgpt-ai-tool-tailored-for-sophisticated-attacks/', 'D'),
        L('DarkBERT (paper)', 'https://arxiv.org/abs/2305.08596', 'D'),
        L('DarkGPT (GitHub)', 'https://github.com/luijait/DarkGPT', 'D'),
        L('WolfGPT (news)', 'https://www.threatintelligence.com/blog/wormgpt-fraudgpt-hackergpt', 'D'),
        L('GhostGPT (news)', 'https://abnormalsecurity.com/blog/ghostgpt-new-uncensored-ai-model', 'D'),
        L('XanthoroxAI (news)', 'https://slashnext.com/blog/xanthorox-ai-the-next-generation-of-malicious-ai-threats-emerges/', 'D'),
        L('PoisonGPT', 'https://blog.mithrilsecurity.io/poisongpt-how-we-hid-a-lobotomized-llm-on-hugging-face-to-spread-fake-news/', 'D'),
        L('ChaosGPT', 'https://github.com/kroll-software/babyagichaosgpt', 'D'),
        L('FreedomGPT', 'https://www.freedomgpt.com/', 'D'),
        L('PentestGPT', 'https://github.com/GreyDGL/PentestGPT', 'D'),
        L('HackerGPT', 'https://www.hackerai.co/', 'D'),
        L('BurpGPT', 'https://github.com/aress31/burpgpt', 'D'),
      ]),
      S('Jailbreak / Prompt DBs', [
        L('FlowGPT', 'https://flowgpt.com/', 'D'),
        L('r/ChatGPTJailbreak', 'https://www.reddit.com/r/ChatGPTJailbreak/', 'D'),
        L('L1B3RT4S (Plinius)', 'https://github.com/elder-plinius/L1B3RT4S', 'D'),
        L('ChatGPT-DAN (GitHub)', 'https://github.com/0xk1h0/ChatGPT_DAN', 'D'),
        L('awesome-chatgpt-prompts', 'https://github.com/f/awesome-chatgpt-prompts', 'T'),
      ]),
      S('API Aggregators / Inference', [
        L('OpenRouter', 'https://openrouter.ai/', 'R'),
        L('Together AI', 'https://www.together.ai/', 'R'),
        L('Groq (fast)', 'https://groq.com/', 'R'),
        L('Fireworks AI', 'https://fireworks.ai/', 'R'),
        L('DeepInfra', 'https://deepinfra.com/', 'R'),
        L('Replicate', 'https://replicate.com/', 'R'),
        L('Cerebras Inference', 'https://cerebras.ai/inference', 'R'),
      ]),
    ]),

    // ────────────────────────── 42. PROMPT ENGINEERING / LEARN ──────────────────────────
    S('Prompt Engineering / Learn', [
      S('Prompt Marketplaces', [
        L('PromptBase', 'https://promptbase.com/', 'M'),
        L('PromptHero', 'https://prompthero.com/', 'T'),
        L('Snack Prompt', 'https://snackprompt.com/', 'T'),
        L('FlowGPT', 'https://flowgpt.com/', 'T'),
        L('AIPRM', 'https://www.aiprm.com/', 'T'),
      ]),
      S('Guides & Courses', [
        L('Learn Prompting', 'https://learnprompting.org/', 'T'),
        L('Prompting Guide (DAIR)', 'https://www.promptingguide.ai/', 'T'),
        L('DeepLearning.AI Prompt Course', 'https://www.deeplearning.ai/short-courses/chatgpt-prompt-engineering-for-developers/', 'T'),
        L('OpenAI Cookbook', 'https://cookbook.openai.com/', 'T'),
        L('Anthropic Prompt Library', 'https://docs.anthropic.com/en/prompt-library/library', 'T'),
        L('Google Prompting Essentials', 'https://grow.google/prompting-essentials/', 'T'),
      ]),
      S('Prompt Collections (GitHub)', [
        L('awesome-chatgpt-prompts', 'https://github.com/f/awesome-chatgpt-prompts', 'T'),
        L('prompts.chat', 'https://prompts.chat/', 'T'),
        L('awesome-prompts', 'https://github.com/ai-boost/awesome-prompts', 'T'),
        L('System Prompts Leaks', 'https://github.com/jujumilk3/leaked-system-prompts', 'D'),
      ]),
      S('Testing / Evaluation', [
        L('PromptLayer', 'https://promptlayer.com/', 'M'),
        L('PromptFoo', 'https://www.promptfoo.dev/', 'T'),
        L('Langfuse', 'https://langfuse.com/', 'T'),
        L('LangSmith', 'https://smith.langchain.com/', 'R'),
      ]),
    ]),

    // ────────────────────────── 43. UNDERGROUND / TIKTOK-VIRAL AI ──────────────────────────
    S('Underground / TikTok-Viral AI', [
      S('Deepfake / Face Swap', [
        L('DeepFaceLab', 'https://github.com/iperov/DeepFaceLab', 'D'),
        L('roop-unleashed', 'https://github.com/C0untFloyd/roop-unleashed', 'D'),
        L('FaceFusion', 'https://github.com/facefusion/facefusion', 'D'),
        L('Reface', 'https://reface.ai/', 'T'),
        L('Remaker Face Swap', 'https://remaker.ai/face-swap-free/', 'D'),
        L('Akool Face Swap', 'https://akool.com/apps/faceswap', 'D'),
      ]),
      S('Nudify (see warnings)', [
        L('SWGfL Nude Imagery Guide', 'https://swgfl.org.uk/magazine/nude-imagery/', 'T'),
        L('StopNCII.org', 'https://stopncii.org/', 'T'),
        L('Take It Down (NCMEC)', 'https://takeitdown.ncmec.org/', 'T'),
      ]),
      S('AI Girlfriend / Companion', [
        L('Replika', 'https://replika.com/', 'M'),
        L('CandyAI', 'https://candy.ai/', 'D'),
        L('DreamGF', 'https://dreamgf.ai/', 'D'),
        L('Kupid AI', 'https://kupid.ai/', 'D'),
        L('Nomi.ai', 'https://nomi.ai/', 'D'),
        L('Muah AI', 'https://muah.ai/', 'D'),
      ]),
      S('Uncensored Image Gen', [
        L('Civitai', 'https://civitai.com/', 'D'),
        L('Pornpen.ai', 'https://pornpen.ai/', 'D'),
        L('Unstable Diffusion', 'https://unstability.ai/', 'D'),
        L('SeaArt (uncensored mode)', 'https://www.seaart.ai/', 'D'),
        L('Yodayo', 'https://yodayo.com/', 'D'),
      ]),
      S('Homework / Exam AI', [
        L('Photomath', 'https://photomath.com/', 'T'),
        L('Caktus AI', 'https://caktus.ai/', 'D'),
        L('StudyX', 'https://studyx.ai/', 'D'),
      ]),
      S('Redteam / Hacker LLMs', [
        L('WormGPT (Wikipedia)', 'https://en.wikipedia.org/wiki/WormGPT', 'D'),
        L('FraudGPT (news)', 'https://www.bleepingcomputer.com/news/security/new-fraudgpt-ai-tool-tailored-for-sophisticated-attacks/', 'D'),
        L('GhostGPT', 'https://abnormalsecurity.com/blog/ghostgpt-new-uncensored-ai-model', 'D'),
        L('PentestGPT', 'https://github.com/GreyDGL/PentestGPT', 'D'),
        L('HackerGPT', 'https://www.hackerai.co/', 'D'),
      ]),
    ]),

    // ────────────────────────── 44. AI DETECTION & ANTI-AI ──────────────────────────
    S('AI Detection & Anti-AI', [
      S('AI Text Detectors', [
        L('GPTZero', 'https://gptzero.me/', 'T'),
        L('Originality.AI', 'https://originality.ai/', 'M'),
        L('Copyleaks', 'https://copyleaks.com/', 'M'),
        L('Turnitin AI', 'https://www.turnitin.com/solutions/ai-writing', 'M'),
        L('Winston AI', 'https://gowinston.ai/', 'M'),
      ]),
      S('Deepfake / Image Detectors', [
        L('Hive Moderation', 'https://hivemoderation.com/ai-generated-content-detection', 'T'),
        L('Sensity AI', 'https://sensity.ai/', 'M'),
        L('Deepware Scanner', 'https://deepware.ai/', 'T'),
        L('AI or Not', 'https://www.aiornot.com/', 'T'),
        L('Reality Defender', 'https://www.realitydefender.com/', 'M'),
      ]),
      S('Humanizers (bypass detectors)', [
        L('Undetectable.ai', 'https://undetectable.ai/', 'D'),
        L('StealthGPT', 'https://www.stealthgpt.ai/', 'D'),
        L('HumanizeAIText', 'https://www.humanizeaitext.ai/', 'D'),
        L('Phrasly', 'https://phrasly.ai/', 'D'),
        L('BypassGPT', 'https://bypassgpt.ai/', 'D'),
      ]),
      S('Watermark / Poisoning', [
        L('Nightshade', 'https://nightshade.cs.uchicago.edu/', 'T'),
        L('Glaze', 'https://glaze.cs.uchicago.edu/', 'T'),
        L('SynthID (DeepMind)', 'https://deepmind.google/technologies/synthid/', 'T'),
      ]),
    ]),

    // ────────────────────────── 45. COMMUNITY & NEWS ──────────────────────────
    S('Community & News', [
      S('Reddit', [
        L('r/LocalLLaMA', 'https://www.reddit.com/r/LocalLLaMA/', 'T'),
        L('r/StableDiffusion', 'https://www.reddit.com/r/StableDiffusion/', 'T'),
        L('r/singularity', 'https://www.reddit.com/r/singularity/', 'T'),
        L('r/OpenAI', 'https://www.reddit.com/r/OpenAI/', 'T'),
        L('r/ClaudeAI', 'https://www.reddit.com/r/ClaudeAI/', 'T'),
        L('r/AItools', 'https://www.reddit.com/r/AItools/', 'T'),
      ]),
      S('Newsletters', [
        L('The Rundown AI', 'https://www.therundown.ai/', 'T'),
        L("Ben's Bites", 'https://bensbites.co/', 'T'),
        L('TLDR AI', 'https://tldr.tech/ai', 'T'),
        L('Superhuman', 'https://www.superhuman.ai/', 'T'),
        L('Import AI (Jack Clark)', 'https://importai.substack.com/', 'T'),
      ]),
      S('Directories', [
        L('Theresanaiforthat (TAAFT)', 'https://theresanaiforthat.com/', 'T'),
        L('Futuretools', 'https://www.futuretools.io/', 'T'),
        L('AI Tool Guru', 'https://aitoolguru.com/', 'T'),
        L('AIToolReport', 'https://aitoolreport.beehiiv.com/', 'T'),
      ]),
      S('YouTube Channels', [
        L('AI Explained', 'https://www.youtube.com/@aiexplained-official', 'T'),
        L('Two Minute Papers', 'https://www.youtube.com/@TwoMinutePapers', 'T'),
        L('Matt Wolfe', 'https://www.youtube.com/@mreflow', 'T'),
        L('MattVidPro AI', 'https://www.youtube.com/@MattVidPro', 'T'),
        L('The AI Advantage', 'https://www.youtube.com/@aiadvantage', 'T'),
      ]),
    ]),

    // ────────────────────────── 46. INVESTING & FINANCE ──────────────────────────
    S('Investing & Finance', [
      S('Stocks', [
        L('Danelfin', 'https://danelfin.com/', 'M'),
        L('AlphaSense', 'https://www.alpha-sense.com/', 'M'),
        L('Kavout', 'https://www.kavout.com/', 'M'),
        L('Trade Ideas', 'https://www.trade-ideas.com/', 'M'),
        L('Tickeron', 'https://tickeron.com/', 'M'),
        L('TrendSpider', 'https://trendspider.com/', 'M'),
        L('Finchat', 'https://finchat.io/', 'M'),
      ]),
      S('Options', [
        L('OptionStrat AI', 'https://optionstrat.com/', 'M'),
        L('Market Chameleon', 'https://marketchameleon.com/', 'M'),
        L('OptionSamurai', 'https://optionsamurai.com/', 'M'),
        L('Unusual Whales', 'https://unusualwhales.com/', 'M'),
      ]),
      S('Futures & Commodities', [
        L('Trading Central', 'https://tradingcentral.com/', 'M'),
        L('Barchart AI', 'https://www.barchart.com/', 'M'),
      ]),
      S('Forex', [
        L('MetaTrader AI Signals', 'https://www.metatrader5.com/', 'M'),
        L('AutoChartist', 'https://www.autochartist.com/', 'M'),
      ]),
      S('Cryptocurrency', [
        L('CoinGecko AI', 'https://www.coingecko.com/', 'T'),
        L('Messari AI', 'https://messari.io/', 'M'),
        L('Nansen', 'https://www.nansen.ai/', 'M'),
        L('Arkham Intelligence', 'https://www.arkhamintelligence.com/', 'R'),
        L('Chainalysis', 'https://www.chainalysis.com/', 'M'),
        L('Token Metrics', 'https://tokenmetrics.com/', 'M'),
        L('Santiment', 'https://santiment.net/', 'M'),
      ]),
      S('ETFs & Bonds', [
        L('ETFDB AI', 'https://etfdb.com/', 'M'),
        L('YCharts', 'https://ycharts.com/', 'M'),
        L('Koyfin AI', 'https://www.koyfin.com/', 'M'),
      ]),
      S('REITs & Real Estate Invest', [
        L('AlphaFlow', 'https://alphaflow.com/', 'M'),
        L('Enodo', 'https://www.enodoinc.com/', 'M'),
        L('HouseCanary', 'https://www.housecanary.com/', 'M'),
      ]),
      S('VC / Startup Research', [
        L('Crunchbase AI', 'https://www.crunchbase.com/', 'M'),
        L('PitchBook AI', 'https://pitchbook.com/', 'M'),
        L('Dealroom', 'https://dealroom.co/', 'M'),
        L('CB Insights', 'https://www.cbinsights.com/', 'M'),
      ]),
      S('Company Financial Analysis', [
        L('AlphaSense', 'https://www.alpha-sense.com/', 'M'),
        L('Finchat', 'https://finchat.io/', 'M'),
        L('Kaiku', 'https://kaiku.ai/', 'M'),
      ]),
      S('Technical Analysis', [
        L('TrendSpider', 'https://trendspider.com/', 'M'),
        L('TradingView AI', 'https://www.tradingview.com/', 'M'),
      ]),
      S('Portfolio Management', [
        L('Composer AI', 'https://www.composer.trade/', 'M'),
        L('Kubera', 'https://www.kubera.com/', 'M'),
      ]),
      S('Banking / Personal Finance', [
        L('Cleo', 'https://web.meetcleo.com/', 'T'),
        L('Rocket Money', 'https://www.rocketmoney.com/', 'M'),
        L('Copilot Money', 'https://copilot.money/', 'M'),
        L('YNAB', 'https://www.ynab.com/', 'M'),
      ]),
      S('Accounting & Tax', [
        L('Truewind', 'https://www.truewind.ai/', 'M'),
        L('Keeper Tax', 'https://www.keepertax.com/', 'M'),
        L('TurboTax AI', 'https://turbotax.intuit.com/', 'M'),
        L('Vic.ai', 'https://www.vic.ai/', 'M'),
      ]),
      S('Insurance', [
        L('Lemonade AI', 'https://www.lemonade.com/', 'M'),
        L('Zelros', 'https://www.zelros.com/', 'M'),
        L('Tractable', 'https://tractable.ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 47. REAL ESTATE (EXPANDED) ──────────────────────────
    S('Real Estate (Full)', [
      S('Residential', [
        L('Zillow Zestimate', 'https://www.zillow.com/z/zestimate/', 'T'),
        L('Redfin Estimate', 'https://www.redfin.com/redfin-estimate', 'T'),
        L('HouseCanary', 'https://www.housecanary.com/', 'M'),
      ]),
      S('Commercial', [
        L('CoStar AI', 'https://www.costar.com/', 'M'),
        L('CREXi', 'https://www.crexi.com/', 'M'),
        L('LoopNet AI', 'https://www.loopnet.com/', 'M'),
      ]),
      S('Property Management', [
        L('AppFolio AI', 'https://www.appfolio.com/', 'M'),
        L('Buildium AI', 'https://www.buildium.com/', 'M'),
        L('EliseAI (leasing)', 'https://www.eliseai.com/', 'M'),
      ]),
      S('House Flipping / Wholesaling', [
        L('DealMachine', 'https://www.dealmachine.com/', 'M'),
        L('PropStream', 'https://www.propstream.com/', 'M'),
        L('BatchLeads', 'https://batchleads.io/', 'M'),
        L('REsimpli', 'https://resimpli.com/', 'M'),
      ]),
      S('BRRRR Strategy', [
        L('BiggerPockets Calculators', 'https://www.biggerpockets.com/calculators', 'M'),
        L('DealCheck', 'https://dealcheck.io/', 'M'),
        L('Rehab Valuator', 'https://rehabvaluator.com/', 'M'),
      ]),
      S('Virtual Staging', [
        L('VirtualStagingAI', 'https://www.virtualstagingai.app/', 'M'),
        L('REimagineHome', 'https://www.reimaginehome.ai/', 'M'),
        L('Styldod', 'https://www.styldod.com/', 'M'),
      ]),
      S('Floor Plans / Renovation', [
        L('Maket', 'https://www.maket.ai/', 'M'),
        L('Planner 5D AI', 'https://planner5d.com/', 'M'),
        L('Interior AI', 'https://interiorai.com/', 'M'),
        L('CubiCasa', 'https://www.cubi.casa/', 'M'),
      ]),
      S('Property Analysis', [
        L('DealCheck', 'https://dealcheck.io/', 'M'),
        L('Roofstock AI', 'https://www.roofstock.com/', 'M'),
        L('PropertyLens', 'https://propertylens.com/', 'M'),
      ]),
      S('Listing Descriptions', [
        L('ListingCopy.ai', 'https://listingcopy.ai/', 'M'),
        L('Rechat', 'https://rechat.com/', 'M'),
        L('WriteListing.com', 'https://writelisting.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 48. CYBERSECURITY & OSINT ──────────────────────────
    S('Cybersecurity & OSINT', [
      S('OSINT', [
        L('OSINT Framework', 'https://osintframework.com/', 'T'),
        L('Maltego', 'https://www.maltego.com/', 'M'),
        L('SpiderFoot', 'https://www.spiderfoot.net/', 'T'),
        L('IntelligenceX', 'https://intelx.io/', 'M'),
        L('Recon-ng', 'https://github.com/lanmaster53/recon-ng', 'T'),
        L('Shodan', 'https://www.shodan.io/', 'R'),
        L('Have I Been Pwned', 'https://haveibeenpwned.com/', 'T'),
        L('OSINT Combine', 'https://www.osintcombine.com/', 'M'),
      ]),
      S('Ethical Hacking / Pen Testing', [
        L('PentestGPT', 'https://github.com/GreyDGL/PentestGPT', 'D'),
        L('HackerGPT', 'https://www.hackerai.co/', 'D'),
        L('BurpGPT', 'https://github.com/aress31/burpgpt', 'D'),
        L('Kali Linux', 'https://www.kali.org/', 'T'),
        L('Metasploit', 'https://www.metasploit.com/', 'T'),
        L('Nuclei AI', 'https://nuclei.projectdiscovery.io/', 'T'),
      ]),
      S('Bug Bounty', [
        L('HackerOne', 'https://www.hackerone.com/', 'R'),
        L('Bugcrowd', 'https://www.bugcrowd.com/', 'R'),
        L('Intigriti', 'https://www.intigriti.com/', 'R'),
        L('YesWeHack', 'https://www.yeswehack.com/', 'R'),
      ]),
      S('Digital Forensics', [
        L('Autopsy', 'https://www.autopsy.com/', 'T'),
        L('Magnet AXIOM', 'https://www.magnetforensics.com/', 'M'),
        L('Cellebrite', 'https://cellebrite.com/', 'M'),
        L('FTK Imager', 'https://www.exterro.com/ftk-imager', 'T'),
      ]),
      S('Malware Analysis', [
        L('VirusTotal', 'https://www.virustotal.com/', 'T'),
        L('Hybrid Analysis', 'https://www.hybrid-analysis.com/', 'T'),
        L('ANY.RUN', 'https://any.run/', 'R'),
        L('Joe Sandbox', 'https://www.joesecurity.org/', 'M'),
        L('MalwareBazaar', 'https://bazaar.abuse.ch/', 'T'),
      ]),
      S('Reverse Engineering', [
        L('Ghidra (NSA)', 'https://ghidra-sre.org/', 'T'),
        L('IDA Pro', 'https://www.hex-rays.com/', 'M'),
        L('Binary Ninja', 'https://binary.ninja/', 'M'),
        L('radare2', 'https://rada.re/', 'T'),
        L('Cutter', 'https://cutter.re/', 'T'),
      ]),
      S('Network Security', [
        L('Wireshark', 'https://www.wireshark.org/', 'T'),
        L('Zeek', 'https://zeek.org/', 'T'),
        L('Snort', 'https://www.snort.org/', 'T'),
        L('Darktrace', 'https://darktrace.com/', 'M'),
      ]),
      S('Threat Intelligence', [
        L('Recorded Future', 'https://www.recordedfuture.com/', 'M'),
        L('Mandiant', 'https://www.mandiant.com/', 'M'),
        L('CrowdStrike Falcon Intel', 'https://www.crowdstrike.com/products/threat-intelligence/', 'M'),
        L('SOCRadar', 'https://socradar.io/', 'M'),
        L('AlienVault OTX', 'https://otx.alienvault.com/', 'T'),
      ]),
      S('Dark Web Monitoring', [
        L('DarkOwl', 'https://www.darkowl.com/', 'M'),
        L('Flashpoint', 'https://flashpoint.io/', 'M'),
        L('Constella', 'https://constellaintelligence.com/', 'M'),
        L('SpyCloud', 'https://spycloud.com/', 'M'),
      ]),
      S('Threat Hunting / SIEM', [
        L('Splunk AI', 'https://www.splunk.com/', 'M'),
        L('Elastic Security', 'https://www.elastic.co/security', 'M'),
        L('SentinelOne Purple AI', 'https://www.sentinelone.com/platform/purple-ai/', 'M'),
      ]),
      S('Incident Response', [
        L('TheHive Project', 'https://thehive-project.org/', 'T'),
        L('Cortex XSOAR', 'https://www.paloaltonetworks.com/cortex/cortex-xsoar', 'M'),
        L('Tines', 'https://www.tines.com/', 'M'),
        L('Torq', 'https://torq.io/', 'M'),
      ]),
      S('Privacy & Encryption', [
        L('Signal', 'https://signal.org/', 'T'),
        L('ProtonMail', 'https://proton.me/mail', 'T'),
        L('Tails OS', 'https://tails.net/', 'T'),
        L('Tor Browser', 'https://www.torproject.org/', 'T'),
        L('VeraCrypt', 'https://www.veracrypt.fr/', 'T'),
      ]),
      S('Compliance', [
        L('Vanta AI', 'https://www.vanta.com/', 'M'),
        L('Drata', 'https://drata.com/', 'M'),
        L('Secureframe', 'https://secureframe.com/', 'M'),
        L('OneTrust', 'https://www.onetrust.com/', 'M'),
      ]),
      S('Missing Persons / Investigations', [
        L('NamUs (US)', 'https://www.namus.gov/', 'T'),
        L('The Charley Project', 'http://charleyproject.org/', 'T'),
        L('PimEyes (face search)', 'https://pimeyes.com/', 'D'),
        L('TinEye', 'https://tineye.com/', 'T'),
      ]),
    ]),

    // ────────────────────────── 49. BY PROFESSION ──────────────────────────
    S('By Profession', [
      S('Teachers / Educators', [
        L('MagicSchool.ai', 'https://www.magicschool.ai/', 'M'),
        L('Curipod', 'https://curipod.com/', 'M'),
        L('Diffit', 'https://beta.diffit.me/', 'T'),
        L('Eduaide.ai', 'https://www.eduaide.ai/', 'M'),
        L('Khanmigo', 'https://www.khanmigo.ai/', 'M'),
      ]),
      S('Realtors', [
        L('Rechat', 'https://rechat.com/', 'M'),
        L('VirtualStagingAI', 'https://www.virtualstagingai.app/', 'M'),
        L('ListingCopy.ai', 'https://listingcopy.ai/', 'M'),
        L('EliseAI', 'https://www.eliseai.com/', 'M'),
      ]),
      S('Lawyers', [
        L('Harvey AI', 'https://www.harvey.ai/', 'M'),
        L('Casetext CoCounsel', 'https://casetext.com/', 'M'),
        L('Spellbook', 'https://www.spellbook.legal/', 'M'),
        L('Lex Machina', 'https://lexmachina.com/', 'M'),
      ]),
      S('Doctors / Clinicians', [
        L('Nuance DAX Copilot', 'https://www.nuance.com/healthcare/dragon-ai-clinical-solutions/dax-copilot.html', 'M'),
        L('Abridge', 'https://www.abridge.com/', 'M'),
        L('Suki AI', 'https://www.suki.ai/', 'M'),
        L('DeepScribe', 'https://www.deepscribe.ai/', 'M'),
      ]),
      S('Engineers', [
        L('Autodesk Fusion AI', 'https://www.autodesk.com/products/fusion-360/', 'M'),
        L('nTop', 'https://www.ntop.com/', 'M'),
        L('Zoo.dev', 'https://zoo.dev/', 'T'),
        L('Cursor', 'https://cursor.com/', 'M'),
      ]),
      S('Marketers', [
        L('Jasper', 'https://www.jasper.ai/', 'M'),
        L('AdCreative.ai', 'https://www.adcreative.ai/', 'M'),
        L('Surfer SEO', 'https://surferseo.com/', 'M'),
        L('HubSpot AI', 'https://www.hubspot.com/artificial-intelligence', 'M'),
      ]),
      S('Traders', [
        L('TrendSpider', 'https://trendspider.com/', 'M'),
        L('Trade Ideas', 'https://www.trade-ideas.com/', 'M'),
        L('Danelfin', 'https://danelfin.com/', 'M'),
        L('Unusual Whales', 'https://unusualwhales.com/', 'M'),
      ]),
      S('Writers / Journalists', [
        L('Sudowrite', 'https://www.sudowrite.com/', 'M'),
        L('NovelCrafter', 'https://novelcrafter.com/', 'M'),
        L('Perplexity', 'https://www.perplexity.ai/', 'T'),
        L('Google NotebookLM', 'https://notebooklm.google.com/', 'T'),
      ]),
      S('Designers', [
        L('Uizard', 'https://uizard.io/', 'M'),
        L('Galileo AI', 'https://www.usegalileo.ai/', 'M'),
        L('Midjourney', 'https://www.midjourney.com/', 'M'),
      ]),
      S('Developers', [
        L('Cursor', 'https://cursor.com/', 'M'),
        L('Claude Code', 'https://www.anthropic.com/claude-code', 'M'),
        L('GitHub Copilot', 'https://github.com/features/copilot', 'M'),
        L('Windsurf', 'https://codeium.com/windsurf', 'T'),
        L('Aider', 'https://aider.chat/', 'T'),
      ]),
      S('Recruiters / HR', [
        L('Fetcher.ai', 'https://fetcher.ai/', 'M'),
        L('SeekOut', 'https://seekout.com/', 'M'),
        L('HireVue', 'https://www.hirevue.com/', 'M'),
        L('Paradox (Olivia)', 'https://www.paradox.ai/', 'M'),
      ]),
      S('Accountants', [
        L('Truewind', 'https://www.truewind.ai/', 'M'),
        L('Vic.ai', 'https://www.vic.ai/', 'M'),
        L('Ramp AI', 'https://ramp.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 50. BY INDUSTRY ──────────────────────────
    S('By Industry', [
      S('Aviation', [
        L('SkyGrid', 'https://skygrid.com/', 'M'),
        L('SITA AI', 'https://www.sita.aero/', 'M'),
        L('Airbus Skywise', 'https://skywise.airbus.com/', 'M'),
      ]),
      S('Space', [
        L('SpaceKnow', 'https://www.spaceknow.com/', 'M'),
        L('Descartes Labs', 'https://www.descarteslabs.com/', 'M'),
        L('Planet Labs', 'https://www.planet.com/', 'M'),
      ]),
      S('Maritime / Shipping', [
        L('Windward', 'https://windward.ai/', 'M'),
        L('Sea Machines', 'https://sea-machines.com/', 'M'),
      ]),
      S('Mining', [
        L('Goldspot Discoveries', 'https://goldspot.ca/', 'M'),
      ]),
      S('Oil & Gas', [
        L('Halliburton DecisionSpace', 'https://www.halliburton.com/', 'M'),
        L('Schlumberger DELFI', 'https://www.software.slb.com/delfi', 'M'),
      ]),
      S('Renewable Energy', [
        L('DeepMind Wind Prediction', 'https://deepmind.google/discover/blog/machine-learning-can-boost-the-value-of-wind-energy/', 'T'),
        L('Amperon', 'https://www.amperon.co/', 'M'),
        L('Bidgely', 'https://www.bidgely.com/', 'M'),
      ]),
      S('Agriculture', [
        L('John Deere AI', 'https://www.deere.com/', 'M'),
        L('Taranis', 'https://www.taranis.com/', 'M'),
        L('Prospera', 'https://www.prospera.ag/', 'M'),
        L('Climate FieldView', 'https://climate.com/', 'M'),
      ]),
      S('Hospitality', [
        L('Duetto', 'https://www.duettocloud.com/', 'M'),
        L('Revinate', 'https://www.revinate.com/', 'M'),
        L('Kipsu', 'https://www.kipsu.com/', 'M'),
      ]),
      S('Restaurants', [
        L('Toast AI', 'https://pos.toasttab.com/', 'M'),
        L('Presto Vision', 'https://presto.com/', 'M'),
        L('7shifts AI', 'https://www.7shifts.com/', 'M'),
      ]),
      S('Retail / Ecommerce', [
        L('Shopify Magic', 'https://www.shopify.com/magic', 'M'),
        L('Klaviyo AI', 'https://www.klaviyo.com/', 'M'),
        L('Rebuy', 'https://www.rebuyengine.com/', 'M'),
        L('Nosto', 'https://www.nosto.com/', 'M'),
      ]),
      S('Sports', [
        L('Stats Perform', 'https://www.statsperform.com/', 'M'),
        L('Second Spectrum', 'https://www.secondspectrum.com/', 'M'),
        L('Hudl AI', 'https://www.hudl.com/', 'M'),
      ]),
      S('Nonprofits', [
        L('Fundraise Up', 'https://fundraiseup.com/', 'M'),
        L('Bloomerang AI', 'https://bloomerang.co/', 'M'),
        L('DonorSearch AI', 'https://www.donorsearch.net/', 'M'),
      ]),
      S('Construction', [
        L('Autodesk Construction Cloud', 'https://construction.autodesk.com/', 'M'),
        L('Buildots', 'https://buildots.com/', 'M'),
        L('OpenSpace', 'https://www.openspace.ai/', 'M'),
      ]),
    ]),

    // ────────────────────────── 51. BY TASK / GOAL ──────────────────────────
    S('By Task / Goal', [
      S('Build a Website', [
        L('v0 by Vercel', 'https://v0.dev/', 'T'),
        L('Lovable', 'https://lovable.dev/', 'T'),
        L('Bolt.new', 'https://bolt.new/', 'T'),
        L('Emergent', 'https://emergent.sh/', 'T'),
        L('Framer AI', 'https://www.framer.com/ai', 'M'),
      ]),
      S('Create a Music Video', [
        L('Neural Frames', 'https://www.neuralframes.com/', 'M'),
        L('Kaiber', 'https://kaiber.ai/', 'M'),
        L('Runway Gen-3', 'https://runwayml.com/', 'M'),
        L('Suno v4', 'https://suno.com/', 'T'),
      ]),
      S('Write a Book', [
        L('Sudowrite', 'https://www.sudowrite.com/', 'M'),
        L('NovelCrafter', 'https://novelcrafter.com/', 'M'),
      ]),
      S('Design a Logo', [
        L('Looka', 'https://looka.com/', 'M'),
        L('Brandmark', 'https://brandmark.io/', 'M'),
        L('LogoAI', 'https://www.logoai.com/', 'M'),
      ]),
      S('Edit a Photo', [
        L('Photoshop Generative Fill', 'https://www.adobe.com/products/photoshop/generative-fill.html', 'M'),
        L('Cleanup.pictures', 'https://cleanup.pictures/', 'T'),
        L('Magnific', 'https://magnific.ai/', 'M'),
      ]),
      S('Analyze Stocks', [
        L('Danelfin', 'https://danelfin.com/', 'M'),
        L('Finchat', 'https://finchat.io/', 'M'),
        L('TrendSpider', 'https://trendspider.com/', 'M'),
      ]),
      S('Design a House / Room', [
        L('REimagineHome', 'https://www.reimaginehome.ai/', 'M'),
        L('Interior AI', 'https://interiorai.com/', 'M'),
        L('Planner 5D AI', 'https://planner5d.com/', 'M'),
      ]),
      S('Learn Python', [
        L('ChatGPT', 'https://chat.openai.com/', 'T'),
        L('Cursor', 'https://cursor.com/', 'M'),
        L('DeepLearning.AI', 'https://www.deeplearning.ai/', 'T'),
      ]),
      S('Find Vulnerabilities', [
        L('PentestGPT', 'https://github.com/GreyDGL/PentestGPT', 'D'),
        L('Nuclei AI', 'https://nuclei.projectdiscovery.io/', 'T'),
        L('Snyk DeepCode', 'https://snyk.io/product/snyk-code/', 'M'),
      ]),
      S('Plan a Vacation', [
        L('Roam Around', 'https://www.roamaround.io/', 'T'),
        L('Wonderplan', 'https://wonderplan.ai/', 'T'),
        L('Layla', 'https://justasklayla.com/', 'T'),
      ]),
      S('Teach Algebra', [
        L('Khanmigo', 'https://www.khanmigo.ai/', 'M'),
        L('Photomath', 'https://photomath.com/', 'T'),
      ]),
      S('Research Genealogy', [
        L('MyHeritage AI', 'https://www.myheritage.com/', 'M'),
        L('Ancestry AI', 'https://www.ancestry.com/', 'M'),
        L('FamilySearch (free)', 'https://www.familysearch.org/', 'T'),
      ]),
      S('Start a Business', [
        L('Upmetrics AI', 'https://upmetrics.co/', 'M'),
        L('15minuteplan.ai', 'https://15minuteplan.ai/', 'M'),
        L('Namelix', 'https://namelix.com/', 'T'),
      ]),
      S('Create a YouTube Channel', [
        L('AutoShorts.ai', 'https://autoshorts.ai/', 'M'),
        L('Opus Clip', 'https://www.opus.pro/', 'M'),
        L('TubeBuddy', 'https://www.tubebuddy.com/', 'M'),
        L('VidIQ', 'https://vidiq.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 52. FAITH & RELIGION ──────────────────────────
    S('Faith & Religion', [
      S('Bible Study (Christian)', [
        L('Bible.ai', 'https://bible.ai/', 'T'),
        L('Logos Bible', 'https://www.logos.com/', 'M'),
        L('YouVersion', 'https://www.bible.com/', 'T'),
        L('Blue Letter Bible', 'https://www.blueletterbible.org/', 'T'),
      ]),
      S('Sermon Preparation', [
        L('Sermonly', 'https://www.sermonly.com/', 'M'),
        L('Pulpit AI', 'https://pulpit.ai/', 'M'),
        L('Sermon.works', 'https://sermon.works/', 'M'),
      ]),
      S('Jewish Studies', [
        L('Sefaria', 'https://www.sefaria.org/', 'T'),
        L('Chabad.org Study', 'https://www.chabad.org/', 'T'),
      ]),
      S('Islamic Studies', [
        L('Quran.com', 'https://quran.com/', 'T'),
        L('Sunnah.com', 'https://sunnah.com/', 'T'),
      ]),
      S('Hindu Studies', [
        L('Vedabase', 'https://vedabase.io/', 'T'),
        L('Sacred-Texts', 'https://sacred-texts.com/hin/', 'T'),
      ]),
      S('Buddhist Resources', [
        L('SuttaCentral', 'https://suttacentral.net/', 'T'),
        L('Access to Insight', 'https://www.accesstoinsight.org/', 'T'),
      ]),
      S('Comparative Religion', [
        L('Sacred-Texts.com', 'https://sacred-texts.com/', 'T'),
        L('Britannica Religion', 'https://www.britannica.com/topic/religion', 'T'),
      ]),
      S('Ancient Languages', [
        L('BibleHub', 'https://biblehub.com/', 'T'),
        L('Perseus Digital Library', 'https://www.perseus.tufts.edu/', 'T'),
      ]),
      S('Theology Research', [
        L('Logos Bible', 'https://www.logos.com/', 'M'),
        L('Semantic Scholar', 'https://www.semanticscholar.org/', 'T'),
      ]),
    ]),

    // ────────────────────────── 53. INTERNATIONAL ──────────────────────────
    S('International', [
      S('Country-Specific AI', [
        L('DeepSeek (China)', 'https://chat.deepseek.com/', 'T'),
        L('Qwen (Alibaba)', 'https://chat.qwen.ai/', 'T'),
        L('Kimi (Moonshot)', 'https://kimi.moonshot.cn/', 'T'),
        L('Mistral (France)', 'https://chat.mistral.ai/', 'T'),
        L('Naver HyperCLOVA X (Korea)', 'https://clova-x.naver.com/', 'R'),
      ]),
      S('Immigration', [
        L('VisaAI', 'https://www.visaai.com/', 'M'),
        L('Boundless AI', 'https://www.boundless.com/', 'M'),
        L('Legalpad', 'https://www.legalpad.io/', 'M'),
      ]),
      S('Cross-Border Commerce', [
        L('Payoneer AI', 'https://www.payoneer.com/', 'M'),
        L('Wise', 'https://wise.com/', 'M'),
        L('Global-e', 'https://www.global-e.com/', 'M'),
      ]),
      S('Currency / FX', [
        L('XE.com', 'https://www.xe.com/', 'T'),
        L('Wise Rates', 'https://wise.com/us/currency-converter/', 'T'),
        L('OANDA', 'https://www.oanda.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 54. LIFESTYLE ──────────────────────────
    S('Lifestyle', [
      S('Fitness', [
        L('Freeletics', 'https://www.freeletics.com/', 'M'),
        L('Fitbod', 'https://www.fitbod.me/', 'M'),
        L('Aaptiv AI', 'https://aaptiv.com/', 'M'),
        L('Whoop', 'https://www.whoop.com/', 'M'),
      ]),
      S('Nutrition', [
        L('MyFitnessPal AI', 'https://www.myfitnesspal.com/', 'M'),
        L('Foodvisor', 'https://www.foodvisor.io/', 'M'),
        L('Lifesum', 'https://lifesum.com/', 'M'),
      ]),
      S('Cooking', [
        L('SideChef', 'https://www.sidechef.com/', 'T'),
        L('Mealime', 'https://www.mealime.com/', 'M'),
        L('DishGen', 'https://dishgen.com/', 'T'),
        L('ChefGPT', 'https://www.chefgpt.xyz/', 'M'),
      ]),
      S('Gardening', [
        L('PictureThis (plant ID)', 'https://www.picturethisai.com/', 'M'),
        L('PlantIn', 'https://myplantin.com/', 'M'),
      ]),
      S('Parenting', [
        L('Winnie', 'https://winnie.com/', 'T'),
        L('Huckleberry', 'https://huckleberrycare.com/', 'M'),
        L('Nanit', 'https://www.nanit.com/', 'M'),
      ]),
      S('Relationships', [
        L('Paired', 'https://www.paired.com/', 'M'),
        L('Lasting', 'https://www.getlasting.com/', 'M'),
      ]),
      S('Mental Wellness / Meditation', [
        L('Calm', 'https://www.calm.com/', 'M'),
        L('Headspace', 'https://www.headspace.com/', 'M'),
        L('Wysa', 'https://www.wysa.com/', 'M'),
        L('Woebot', 'https://woebothealth.com/', 'M'),
      ]),
      S('Travel', [
        L('Roam Around', 'https://www.roamaround.io/', 'T'),
        L('Wonderplan', 'https://wonderplan.ai/', 'T'),
        L('Layla', 'https://justasklayla.com/', 'T'),
      ]),
      S('Pets', [
        L('TryFi', 'https://tryfi.com/', 'M'),
        L('Petpuls', 'https://petpuls.com/', 'M'),
        L('Whistle', 'https://www.whistle.com/', 'M'),
      ]),
      S('Automotive', [
        L('CarGPT', 'https://cargpt.com/', 'M'),
        L('CarEdge', 'https://caredge.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 55. GOVERNMENT / PUBLIC SECTOR ──────────────────────────
    S('Government / Public Sector', [
      S('Military / Defense', [
        L('Palantir Foundry', 'https://www.palantir.com/platforms/foundry/', 'M'),
        L('Anduril', 'https://www.anduril.com/', 'M'),
        L('Shield AI', 'https://shield.ai/', 'M'),
        L('Scale AI Defense', 'https://scale.com/defense', 'M'),
      ]),
      S('Law Enforcement', [
        L('Axon (body cam analytics)', 'https://www.axon.com/', 'M'),
        L('Peregrine', 'https://www.peregrine.io/', 'M'),
        L('Veritone', 'https://www.veritone.com/', 'M'),
      ]),
      S('Emergency Services', [
        L('RapidSOS', 'https://rapidsos.com/', 'M'),
        L('Prepared', 'https://www.prepared911.com/', 'M'),
        L('Carbyne', 'https://carbyne.com/', 'M'),
      ]),
      S('Government AI Platforms', [
        L('Palantir Gotham', 'https://www.palantir.com/platforms/gotham/', 'M'),
        L('AI.gov (US)', 'https://ai.gov/', 'T'),
        L('data.gov', 'https://www.data.gov/', 'T'),
      ]),
    ]),

    // ────────────────────────── 56. SCIENCE ──────────────────────────
    S('Science', [
      S('Space & Astronomy', [
        L('NASA ADS', 'https://ui.adsabs.harvard.edu/', 'T'),
        L('Stellarium Web', 'https://stellarium-web.org/', 'T'),
        L('Space-Track.org', 'https://www.space-track.org/', 'R'),
        L('SETI@home / Berkeley AI', 'https://setiathome.berkeley.edu/', 'T'),
        L('SkyView (NASA)', 'https://skyview.gsfc.nasa.gov/', 'T'),
      ]),
      S('Psychology', [
        L('Psycnet (APA)', 'https://psycnet.apa.org/', 'M'),
        L('Google Scholar Psych', 'https://scholar.google.com/', 'T'),
        L('Woebot', 'https://woebothealth.com/', 'M'),
        L('Wysa', 'https://www.wysa.com/', 'M'),
      ]),
      S('Philosophy', [
        L('Stanford Encyclopedia of Philosophy', 'https://plato.stanford.edu/', 'T'),
        L('PhilPapers', 'https://philpapers.org/', 'T'),
        L('IEP', 'https://iep.utm.edu/', 'T'),
      ]),
      S('Anatomy', [
        L('Complete Anatomy (Elsevier)', 'https://www.elsevier.com/products/complete-anatomy', 'M'),
        L('BioDigital', 'https://www.biodigital.com/', 'M'),
        L('Kenhub', 'https://www.kenhub.com/', 'M'),
        L('Anatomy Zone', 'https://anatomyzone.com/', 'T'),
      ]),
      S('Neurology & Neuroscience', [
        L('BrainFacts', 'https://www.brainfacts.org/', 'T'),
        L('Allen Brain Atlas', 'https://portal.brain-map.org/', 'T'),
        L('Nengo', 'https://www.nengo.ai/', 'T'),
      ]),
      S('Biology', [
        L('NCBI (PubMed)', 'https://pubmed.ncbi.nlm.nih.gov/', 'T'),
        L('BioRxiv', 'https://www.biorxiv.org/', 'T'),
        L('OpenAI Bio (BioGPT)', 'https://github.com/microsoft/BioGPT', 'T'),
      ]),
      S('Marine Biology', [
        L('Ocean Biodiversity Info', 'https://obis.org/', 'T'),
        L('MBARI', 'https://www.mbari.org/', 'T'),
        L('FishBase', 'https://www.fishbase.se/', 'T'),
      ]),
      S('Chemistry', [
        L('PubChem', 'https://pubchem.ncbi.nlm.nih.gov/', 'T'),
        L('RDKit', 'https://www.rdkit.org/', 'T'),
        L('ChemCrow', 'https://github.com/ur-whitelab/chemcrow-public', 'T'),
        L('Reaxys', 'https://www.reaxys.com/', 'M'),
      ]),
      S('Physics', [
        L('arXiv Physics', 'https://arxiv.org/list/physics/new', 'T'),
        L('Physics.AI', 'https://www.physics.ai/', 'M'),
        L('Wolfram Alpha Physics', 'https://www.wolframalpha.com/', 'T'),
      ]),
      S('Quantum Computing', [
        L('IBM Quantum', 'https://quantum.ibm.com/', 'R'),
        L('Google Quantum AI', 'https://quantumai.google/', 'R'),
        L('Qiskit', 'https://www.ibm.com/quantum/qiskit', 'T'),
        L('PennyLane', 'https://pennylane.ai/', 'T'),
      ]),
      S('Mathematics', [
        L('Wolfram Alpha', 'https://www.wolframalpha.com/', 'T'),
        L('Symbolab', 'https://www.symbolab.com/', 'M'),
        L('Mathpix', 'https://mathpix.com/', 'M'),
        L('OpenAI Math Solver (o1)', 'https://openai.com/o1/', 'M'),
      ]),
      S('Statistics', [
        L('R', 'https://www.r-project.org/', 'T'),
        L('JASP', 'https://jasp-stats.org/', 'T'),
        L('Julius AI', 'https://julius.ai/', 'M'),
      ]),
      S('Genetics / Genomics', [
        L('AlphaFold 3', 'https://alphafoldserver.com/', 'R'),
        L('Ensembl', 'https://ensembl.org/', 'T'),
        L('NCBI Genome', 'https://www.ncbi.nlm.nih.gov/genome/', 'T'),
        L('DeepVariant', 'https://github.com/google/deepvariant', 'T'),
        L('Illumina DRAGEN AI', 'https://www.illumina.com/products/by-type/informatics-products/dragen-bio-it-platform.html', 'M'),
      ]),
      S('Materials Science', [
        L('Materials Project', 'https://next-gen.materialsproject.org/', 'T'),
        L('MatCloud', 'https://www.matcloud.cn/', 'M'),
        L('MatterGen (Microsoft)', 'https://www.microsoft.com/en-us/research/blog/mattergen-a-generative-model-for-inorganic-materials-design/', 'T'),
      ]),
      S('Oceanography', [
        L('NOAA', 'https://www.noaa.gov/', 'T'),
        L('Copernicus Marine', 'https://marine.copernicus.eu/', 'T'),
        L('Ocean Biodiversity Info', 'https://obis.org/', 'T'),
      ]),
      S('Environmental Science', [
        L('Global Forest Watch', 'https://www.globalforestwatch.org/', 'T'),
        L('Climate TRACE', 'https://climatetrace.org/', 'T'),
        L('EarthTime (CMU)', 'https://earthtime.org/', 'T'),
      ]),
      S('Weather & Climate', [
        L('ECMWF (Copernicus)', 'https://climate.copernicus.eu/', 'T'),
        L('Windy', 'https://www.windy.com/', 'T'),
        L('GraphCast (DeepMind)', 'https://deepmind.google/discover/blog/graphcast-ai-model-for-faster-and-more-accurate-global-weather-forecasting/', 'T'),
        L('Tomorrow.io', 'https://www.tomorrow.io/', 'M'),
      ]),
    ]),

    // ────────────────────────── 57. HUMANITIES & SOCIAL SCIENCES ──────────────────────────
    S('Humanities & Social Sciences', [
      S('History', [
        L('JSTOR', 'https://www.jstor.org/', 'R'),
        L('Perseus Digital Library', 'https://www.perseus.tufts.edu/', 'T'),
        L('Internet History Sourcebooks', 'https://sourcebooks.fordham.edu/', 'T'),
        L('Wikipedia History Portal', 'https://en.wikipedia.org/wiki/Portal:History', 'T'),
      ]),
      S('Archaeology', [
        L('ARIADNE Portal', 'https://portal.ariadne-infrastructure.eu/', 'T'),
        L('Open Context', 'https://opencontext.org/', 'T'),
      ]),
      S('Anthropology', [
        L('HRAF (eHRAF)', 'https://ehrafworldcultures.yale.edu/', 'M'),
        L('AnthroSource', 'https://anthrosource.onlinelibrary.wiley.com/', 'M'),
      ]),
      S('Geography', [
        L('OpenStreetMap', 'https://www.openstreetmap.org/', 'T'),
        L('Mapbox AI', 'https://www.mapbox.com/', 'M'),
        L('ArcGIS AI', 'https://www.esri.com/en-us/arcgis/ai-in-gis/', 'M'),
      ]),
      S('Geology', [
        L('USGS Data', 'https://www.usgs.gov/products/data', 'T'),
        L('Macrostrat', 'https://macrostrat.org/', 'T'),
      ]),
      S('Sociology', [
        L('SocArXiv', 'https://socopen.org/', 'T'),
        L('ICPSR', 'https://www.icpsr.umich.edu/', 'R'),
      ]),
      S('Economics', [
        L('FRED (St. Louis Fed)', 'https://fred.stlouisfed.org/', 'T'),
        L('World Bank Data', 'https://data.worldbank.org/', 'T'),
        L('Trading Economics', 'https://tradingeconomics.com/', 'M'),
      ]),
      S('Political Science', [
        L('V-Dem Institute', 'https://www.v-dem.net/', 'T'),
        L('Comparative Political Data', 'https://www.cpds-data.org/', 'T'),
        L('Ballotpedia', 'https://ballotpedia.org/', 'T'),
      ]),
      S('Linguistics', [
        L('Ethnologue', 'https://www.ethnologue.com/', 'M'),
        L('WALS (World Atlas of Language Structures)', 'https://wals.info/', 'T'),
        L('CLC (Cambridge Corpus)', 'https://www.cambridge.org/us/cambridgeenglish/', 'M'),
      ]),
      S('Libraries & Academic Resources', [
        L('Google Scholar', 'https://scholar.google.com/', 'T'),
        L('Semantic Scholar', 'https://www.semanticscholar.org/', 'T'),
        L('Internet Archive', 'https://archive.org/', 'T'),
        L('CORE', 'https://core.ac.uk/', 'T'),
        L('DOAJ', 'https://doaj.org/', 'T'),
      ]),
      S('Mythology', [
        L('Theoi.com (Greek)', 'https://www.theoi.com/', 'T'),
        L('World History Encyclopedia', 'https://www.worldhistory.org/', 'T'),
        L('Sacred-Texts.com', 'https://sacred-texts.com/', 'T'),
      ]),
      S('Spirituality', [
        L('Insight Timer', 'https://insighttimer.com/', 'T'),
        L('Calm', 'https://www.calm.com/', 'M'),
        L('Headspace', 'https://www.headspace.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 58. MEDICINE & HEALTHCARE ──────────────────────────
    S('Medicine & Healthcare', [
      S('Medical Professionals', [
        L('UpToDate', 'https://www.wolterskluwer.com/en/solutions/uptodate', 'M'),
        L('Nuance DAX Copilot', 'https://www.nuance.com/healthcare/dragon-ai-clinical-solutions/dax-copilot.html', 'M'),
        L('OpenEvidence', 'https://www.openevidence.com/', 'R'),
        L('Glass Health', 'https://glass.health/', 'R'),
      ]),
      S('Nursing', [
        L('Nurse.com', 'https://www.nurse.com/', 'T'),
        L('AACN Practice Tools', 'https://www.aacnnursing.org/', 'T'),
      ]),
      S('Dentistry', [
        L('Pearl AI', 'https://www.hellopearl.com/', 'M'),
        L('Overjet', 'https://www.overjet.ai/', 'M'),
        L('VideaHealth', 'https://www.videa.ai/', 'M'),
      ]),
      S('Veterinary Medicine', [
        L('Vetology AI', 'https://www.vetology.ai/', 'M'),
        L('SignalPET', 'https://signalpet.com/', 'M'),
        L('VeterinaryPartner', 'https://veterinarypartner.vin.com/', 'T'),
      ]),
      S('Mental Health', [
        L('Woebot', 'https://woebothealth.com/', 'M'),
        L('Wysa', 'https://www.wysa.com/', 'M'),
        L('Talkspace', 'https://www.talkspace.com/', 'M'),
        L('BetterHelp', 'https://www.betterhelp.com/', 'M'),
      ]),
      S('Public Health', [
        L('WHO Data', 'https://data.who.int/', 'T'),
        L('CDC Data', 'https://data.cdc.gov/', 'T'),
        L('Our World in Data', 'https://ourworldindata.org/', 'T'),
      ]),
      S('Nutrition', [
        L('Cronometer', 'https://cronometer.com/', 'M'),
        L('MyFitnessPal AI', 'https://www.myfitnesspal.com/', 'M'),
        L('Foodvisor', 'https://www.foodvisor.io/', 'M'),
      ]),
      S('Exercise Science', [
        L('Whoop', 'https://www.whoop.com/', 'M'),
        L('Strava AI', 'https://www.strava.com/', 'M'),
        L('Fitbod', 'https://www.fitbod.me/', 'M'),
      ]),
    ]),

    // ────────────────────────── 59. ENGINEERING DISCIPLINES ──────────────────────────
    S('Engineering Disciplines', [
      S('Civil Engineering', [
        L('Autodesk Civil 3D AI', 'https://www.autodesk.com/products/civil-3d/', 'M'),
        L('Bentley OpenRoads', 'https://www.bentley.com/software/openroads-designer/', 'M'),
        L('Trimble AI', 'https://www.trimble.com/', 'M'),
      ]),
      S('Mechanical Engineering', [
        L('Autodesk Fusion AI', 'https://www.autodesk.com/products/fusion-360/', 'M'),
        L('SolidWorks AI', 'https://www.solidworks.com/', 'M'),
        L('PTC Creo AI', 'https://www.ptc.com/en/products/creo', 'M'),
        L('nTop', 'https://www.ntop.com/', 'M'),
      ]),
      S('Electrical Engineering', [
        L('Altium AI', 'https://www.altium.com/', 'M'),
        L('KiCad', 'https://www.kicad.org/', 'T'),
        L('MATLAB Simulink AI', 'https://www.mathworks.com/products/simulink.html', 'M'),
      ]),
      S('Aerospace Engineering', [
        L('ANSYS AI', 'https://www.ansys.com/', 'M'),
        L('OpenVSP', 'https://openvsp.org/', 'T'),
        L('Siemens NX', 'https://www.plm.automation.siemens.com/', 'M'),
      ]),
      S('Chemical Engineering', [
        L('Aspen Plus', 'https://www.aspentech.com/', 'M'),
        L('COMSOL Multiphysics', 'https://www.comsol.com/', 'M'),
      ]),
      S('Industrial Engineering', [
        L('AnyLogic', 'https://www.anylogic.com/', 'M'),
        L('Arena Simulation', 'https://www.rockwellautomation.com/en-us/products/software/arena-simulation.html', 'M'),
        L('FlexSim', 'https://www.flexsim.com/', 'M'),
      ]),
      S('Robotics', [
        L('NVIDIA Isaac', 'https://developer.nvidia.com/isaac', 'R'),
        L('MoveIt', 'https://moveit.ai/', 'T'),
        L('ROS', 'https://www.ros.org/', 'T'),
      ]),
      S('Urban Planning', [
        L('Autodesk Forma', 'https://www.autodesk.com/products/forma', 'M'),
        L('UrbanFootprint', 'https://urbanfootprint.com/', 'M'),
        L('Sidewalk Labs (Delve)', 'https://www.sidewalklabs.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 60. NATURE & OUTDOORS ──────────────────────────
    S('Nature & Outdoors', [
      S('Gardening', [
        L('PictureThis (plant ID)', 'https://www.picturethisai.com/', 'M'),
        L('PlantIn', 'https://myplantin.com/', 'M'),
        L('Seek by iNaturalist', 'https://www.inaturalist.org/pages/seek_app', 'T'),
      ]),
      S('Homesteading', [
        L('The Old Farmer\u2019s Almanac', 'https://www.almanac.com/', 'T'),
        L('Morning Chores', 'https://morningchores.com/', 'T'),
      ]),
      S('Survival & Preparedness', [
        L('Ready.gov', 'https://www.ready.gov/', 'T'),
        L('Red Cross Emergency App', 'https://www.redcross.org/get-help/how-to-prepare-for-emergencies/mobile-apps.html', 'T'),
        L('SAS Survival Guide App', 'https://www.trekker.co.uk/sas-survival/', 'M'),
      ]),
      S('Wildlife & Nature', [
        L('iNaturalist (AI ID)', 'https://www.inaturalist.org/', 'T'),
        L('Merlin Bird ID (Cornell)', 'https://merlin.allaboutbirds.org/', 'T'),
        L('eBird', 'https://ebird.org/', 'T'),
        L('BirdNET', 'https://birdnet.cornell.edu/', 'T'),
      ]),
      S('Forestry', [
        L('Global Forest Watch', 'https://www.globalforestwatch.org/', 'T'),
        L('SilviaTerra', 'https://silviaterra.com/', 'M'),
        L('Pachama', 'https://pachama.com/', 'M'),
      ]),
      S('Agriculture', [
        L('John Deere AI', 'https://www.deere.com/', 'M'),
        L('Taranis', 'https://www.taranis.com/', 'M'),
        L('Prospera', 'https://www.prospera.ag/', 'M'),
        L('Climate FieldView', 'https://climate.com/', 'M'),
      ]),
      S('Sustainability & Green Tech', [
        L('Climate TRACE', 'https://climatetrace.org/', 'T'),
        L('Watershed', 'https://watershed.com/', 'M'),
        L('Persefoni', 'https://persefoni.com/', 'M'),
        L('Sylvera', 'https://www.sylvera.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 61. CREATIVE DISCIPLINES ──────────────────────────
    S('Creative Disciplines', [
      S('Architecture (Discipline)', [
        L('Veras (Evolvelab)', 'https://www.evolvelab.io/veras', 'M'),
        L('Autodesk Forma', 'https://www.autodesk.com/products/forma', 'M'),
        L('ArchitectGPT', 'https://architectgpt.io/', 'M'),
      ]),
      S('Interior Design', [
        L('REimagineHome', 'https://www.reimaginehome.ai/', 'M'),
        L('Interior AI', 'https://interiorai.com/', 'M'),
        L('Planner 5D AI', 'https://planner5d.com/', 'M'),
      ]),
      S('Fashion (Discipline)', [
        L('CALA', 'https://ca.la/', 'M'),
        L('Refabric', 'https://refabric.com/', 'M'),
        L('CLO 3D', 'https://www.clo3d.com/', 'M'),
      ]),
      S('Culinary Arts', [
        L('SideChef', 'https://www.sidechef.com/', 'T'),
        L('DishGen', 'https://dishgen.com/', 'T'),
        L('ChefGPT', 'https://www.chefgpt.xyz/', 'M'),
      ]),
      S('Photography (Discipline)', [
        L('Adobe Lightroom AI', 'https://www.adobe.com/products/photoshop-lightroom.html', 'M'),
        L('Topaz Photo AI', 'https://www.topazlabs.com/topaz-photo-ai', 'M'),
        L('Luminar Neo', 'https://skylum.com/luminar', 'M'),
      ]),
      S('Film', [
        L('LTX Studio', 'https://ltx.studio/', 'R'),
        L('Runway Gen-3', 'https://runwayml.com/', 'M'),
        L('Filmustage', 'https://filmustage.com/', 'M'),
      ]),
      S('Music Theory', [
        L('Hooktheory', 'https://www.hooktheory.com/', 'M'),
        L('musictheory.net', 'https://www.musictheory.net/', 'T'),
        L('Odla AI', 'https://odla.ai/', 'M'),
      ]),
      S('Performing Arts', [
        L('Fisher Center Digital Stage', 'https://fishercenter.bard.edu/', 'T'),
        L('Motion Bank', 'https://motionbank.org/', 'T'),
      ]),
    ]),

    // ────────────────────────── 62. EDUCATION (EXPANDED) ──────────────────────────
    S('Education (Expanded)', [
      S('Teachers (K-12)', [
        L('MagicSchool.ai', 'https://www.magicschool.ai/', 'M'),
        L('Curipod', 'https://curipod.com/', 'M'),
        L('Diffit', 'https://beta.diffit.me/', 'T'),
        L('Eduaide.ai', 'https://www.eduaide.ai/', 'M'),
      ]),
      S('Students', [
        L('Photomath', 'https://photomath.com/', 'T'),
        L('Khanmigo', 'https://www.khanmigo.ai/', 'M'),
        L('Wolfram Alpha', 'https://www.wolframalpha.com/', 'T'),
        L('Google NotebookLM', 'https://notebooklm.google.com/', 'T'),
      ]),
      S('Research', [
        L('Elicit', 'https://elicit.com/', 'T'),
        L('Consensus', 'https://consensus.app/', 'T'),
        L('SciSpace', 'https://scispace.com/', 'T'),
        L('ResearchRabbit', 'https://www.researchrabbit.ai/', 'T'),
      ]),
      S('Libraries & Academic Resources', [
        L('Google Scholar', 'https://scholar.google.com/', 'T'),
        L('Semantic Scholar', 'https://www.semanticscholar.org/', 'T'),
        L('Internet Archive', 'https://archive.org/', 'T'),
        L('JSTOR', 'https://www.jstor.org/', 'R'),
        L('CORE', 'https://core.ac.uk/', 'T'),
      ]),
    ]),

    // ────────────────────────── 63. MONEY & CAREERS ──────────────────────────
    S('Money & Careers', [
      S('Side Gigs', [
        L('Fiverr', 'https://www.fiverr.com/', 'R'),
        L('Upwork', 'https://www.upwork.com/', 'R'),
        L('TaskRabbit', 'https://www.taskrabbit.com/', 'R'),
        L('Rover (pet care)', 'https://www.rover.com/', 'R'),
        L('Instacart Shopper', 'https://shoppers.instacart.com/', 'R'),
      ]),
      S('Remote Jobs', [
        L('We Work Remotely', 'https://weworkremotely.com/', 'T'),
        L('Remote OK', 'https://remoteok.com/', 'T'),
        L('Remotive', 'https://remotive.com/', 'T'),
        L('AngelList / Wellfound', 'https://wellfound.com/', 'R'),
        L('Working Nomads', 'https://www.workingnomads.com/', 'T'),
      ]),
      S('Freelance Work', [
        L('Upwork', 'https://www.upwork.com/', 'R'),
        L('Fiverr', 'https://www.fiverr.com/', 'R'),
        L('Toptal', 'https://www.toptal.com/', 'R'),
        L('Contra', 'https://contra.com/', 'R'),
      ]),
      S('AI Jobs', [
        L('AI Jobs List', 'https://aijobs.net/', 'T'),
        L('AI-Jobs.net', 'https://ai-jobs.net/', 'T'),
        L('Kaggle Jobs', 'https://www.kaggle.com/jobs', 'T'),
        L('Hugging Face Jobs', 'https://huggingface.co/jobs', 'T'),
      ]),
      S('Career Development', [
        L('LinkedIn Learning AI', 'https://www.linkedin.com/learning/', 'M'),
        L('Coursera Career', 'https://www.coursera.org/', 'M'),
        L('Codecademy AI', 'https://www.codecademy.com/', 'M'),
      ]),
      S('Resume Builders', [
        L('Kickresume', 'https://www.kickresume.com/', 'M'),
        L('Rezi.ai', 'https://www.rezi.ai/', 'M'),
        L('Enhancv AI', 'https://enhancv.com/', 'M'),
        L('Teal', 'https://www.tealhq.com/', 'M'),
        L('Resume Worded', 'https://resumeworded.com/', 'M'),
      ]),
      S('Interview Prep', [
        L('Interview Warmup (Google)', 'https://grow.google/certificates/interview-warmup/', 'T'),
        L('Interviewing.io', 'https://interviewing.io/', 'M'),
        L('Pramp', 'https://www.pramp.com/', 'T'),
        L('Yoodli AI', 'https://yoodli.ai/', 'M'),
      ]),
      S('Salary Negotiation', [
        L('Levels.fyi', 'https://www.levels.fyi/', 'T'),
        L('Payscale', 'https://www.payscale.com/', 'T'),
        L('Glassdoor Salaries', 'https://www.glassdoor.com/', 'T'),
      ]),
      S('Passive Income Ideas', [
        L('Nomad List', 'https://nomadlist.com/', 'M'),
        L('Product Hunt Ship', 'https://www.producthunt.com/', 'T'),
        L('Indie Hackers', 'https://www.indiehackers.com/', 'T'),
      ]),
      S('Small Business / Entrepreneurship', [
        L('Upmetrics AI', 'https://upmetrics.co/', 'M'),
        L('15minuteplan.ai', 'https://15minuteplan.ai/', 'M'),
        L('Namelix', 'https://namelix.com/', 'T'),
        L('Y Combinator Startup School', 'https://www.startupschool.org/', 'T'),
      ]),
      S('Grants & Funding', [
        L('Grants.gov', 'https://www.grants.gov/', 'T'),
        L('Instrumentl', 'https://www.instrumentl.com/', 'M'),
        L('Candid (Foundation Directory)', 'https://candid.org/', 'M'),
        L('SBA Loans', 'https://www.sba.gov/funding-programs/loans', 'T'),
      ]),
    ]),

    // ────────────────────────── 64. COMMUNITY & CLASSIFIEDS ──────────────────────────
    S('Community & Classifieds', [
      S('Looking for Work', [
        L('LinkedIn', 'https://www.linkedin.com/', 'R'),
        L('Indeed', 'https://www.indeed.com/', 'T'),
        L('Wellfound (AngelList)', 'https://wellfound.com/', 'R'),
        L('Hacker News Who\u2019s Hiring', 'https://news.ycombinator.com/', 'T'),
      ]),
      S('Hiring', [
        L('Ashby', 'https://www.ashbyhq.com/', 'M'),
        L('Greenhouse', 'https://www.greenhouse.io/', 'M'),
        L('Workable', 'https://www.workable.com/', 'M'),
        L('Lever', 'https://www.lever.co/', 'M'),
      ]),
      S('Find a Co-Founder', [
        L('YC Co-Founder Matching', 'https://www.startupschool.org/cofounder-matching', 'T'),
        L('CoFoundersLab', 'https://cofounderslab.com/', 'R'),
        L('Indie Hackers', 'https://www.indiehackers.com/', 'T'),
      ]),
      S('Buy & Sell / Marketplace', [
        L('Craigslist', 'https://www.craigslist.org/', 'T'),
        L('Facebook Marketplace', 'https://www.facebook.com/marketplace/', 'R'),
        L('OfferUp', 'https://offerup.com/', 'R'),
        L('Nextdoor', 'https://nextdoor.com/', 'R'),
      ]),
      S('Local Services', [
        L('Thumbtack', 'https://www.thumbtack.com/', 'R'),
        L('Nextdoor', 'https://nextdoor.com/', 'R'),
        L('TaskRabbit', 'https://www.taskrabbit.com/', 'R'),
      ]),
      S('Collaboration Requests', [
        L('IndieHackers Requests', 'https://www.indiehackers.com/', 'T'),
        L('Reddit r/forhire', 'https://www.reddit.com/r/forhire/', 'T'),
        L('Reddit r/slavelabour', 'https://www.reddit.com/r/slavelabour/', 'T'),
      ]),
    ]),

    // ────────────────────────── 65. RESEARCH & LEARNING (EXPANDED) ──────────────────────────
    S('Research & Learning', [
      S('Digital Archives', [
        L('Internet Archive', 'https://archive.org/', 'T'),
        L('Wayback Machine', 'https://web.archive.org/', 'T'),
        L('Library of Congress Digital Collections', 'https://www.loc.gov/collections/', 'T'),
        L('DPLA', 'https://dp.la/', 'T'),
      ]),
      S('Museums (Online)', [
        L('Google Arts & Culture', 'https://artsandculture.google.com/', 'T'),
        L('Smithsonian Open Access', 'https://www.si.edu/openaccess', 'T'),
        L('The Met', 'https://www.metmuseum.org/', 'T'),
        L('British Museum', 'https://www.britishmuseum.org/', 'T'),
      ]),
      S('Academic Papers', [
        L('arXiv', 'https://arxiv.org/', 'T'),
        L('Semantic Scholar', 'https://www.semanticscholar.org/', 'T'),
        L('CORE', 'https://core.ac.uk/', 'T'),
        L('DOAJ', 'https://doaj.org/', 'T'),
        L('SSRN', 'https://www.ssrn.com/', 'T'),
      ]),
      S('Case Studies', [
        L('Harvard Business Cases', 'https://www.hbs.edu/faculty/Pages/cases.aspx', 'M'),
        L('CB Insights', 'https://www.cbinsights.com/', 'M'),
        L('Failory', 'https://www.failory.com/', 'T'),
      ]),
      S('Research Databases', [
        L('Google Scholar', 'https://scholar.google.com/', 'T'),
        L('JSTOR', 'https://www.jstor.org/', 'R'),
        L('PubMed', 'https://pubmed.ncbi.nlm.nih.gov/', 'T'),
        L('Web of Science', 'https://www.webofscience.com/', 'M'),
        L('Scopus', 'https://www.scopus.com/', 'M'),
      ]),
      S('Government Resources', [
        L('USA.gov', 'https://www.usa.gov/', 'T'),
        L('data.gov', 'https://www.data.gov/', 'T'),
        L('EU Open Data Portal', 'https://data.europa.eu/', 'T'),
        L('UK Gov data', 'https://data.gov.uk/', 'T'),
        L('OECD Data', 'https://data.oecd.org/', 'T'),
      ]),
      S('Open Data', [
        L('Kaggle Datasets', 'https://www.kaggle.com/datasets', 'T'),
        L('Google Dataset Search', 'https://datasetsearch.research.google.com/', 'T'),
        L('AWS Open Data', 'https://registry.opendata.aws/', 'T'),
        L('Hugging Face Datasets', 'https://huggingface.co/datasets', 'T'),
      ]),
      S('Statistics', [
        L('FRED', 'https://fred.stlouisfed.org/', 'T'),
        L('World Bank Data', 'https://data.worldbank.org/', 'T'),
        L('BLS', 'https://www.bls.gov/data/', 'T'),
        L('Statista', 'https://www.statista.com/', 'M'),
      ]),
    ]),

    // ────────────────────────── 66. REVIEWS ──────────────────────────
    S('Reviews', [
      S('AI Tool Directories', [
        L('Theresanaiforthat (TAAFT)', 'https://theresanaiforthat.com/', 'T'),
        L('Futuretools', 'https://www.futuretools.io/', 'T'),
        L('Futurepedia', 'https://www.futurepedia.io/', 'T'),
        L('AI Tool Guru', 'https://aitoolguru.com/', 'T'),
      ]),
      S('User Ratings', [
        L('Product Hunt', 'https://www.producthunt.com/', 'T'),
        L('G2', 'https://www.g2.com/', 'T'),
        L('Capterra', 'https://www.capterra.com/', 'T'),
        L('TrustRadius', 'https://www.trustradius.com/', 'T'),
      ]),
      S('Expert Reviews', [
        L('The Verge AI', 'https://www.theverge.com/ai-artificial-intelligence', 'T'),
        L('Ars Technica AI', 'https://arstechnica.com/ai/', 'T'),
        L('TechCrunch AI', 'https://techcrunch.com/category/artificial-intelligence/', 'T'),
      ]),
      S('Comparisons / Alternatives', [
        L('AlternativeTo', 'https://alternativeto.net/', 'T'),
        L('G2 Compare', 'https://www.g2.com/compare', 'T'),
        L('SaaSHub', 'https://www.saashub.com/', 'T'),
      ]),
      S("Top 10 / Best-Of Lists", [
        L('Zapier Best AI', 'https://zapier.com/blog/best-ai-tools/', 'T'),
        L('Tom\u2019s Guide Best AI', 'https://www.tomsguide.com/tag/ai', 'T'),
        L("PCMag Editors' Choice", 'https://www.pcmag.com/', 'T'),
      ]),
    ]),

    // ────────────────────────── 67. NEWS ──────────────────────────
    S('News', [
      S('AI News', [
        L('The Rundown AI', 'https://www.therundown.ai/', 'T'),
        L("Ben\u2019s Bites", 'https://bensbites.co/', 'T'),
        L('TLDR AI', 'https://tldr.tech/ai', 'T'),
        L('Import AI (Jack Clark)', 'https://importai.substack.com/', 'T'),
        L('MarkTechPost', 'https://www.marktechpost.com/', 'T'),
      ]),
      S('Tech News', [
        L('Hacker News', 'https://news.ycombinator.com/', 'T'),
        L('The Verge', 'https://www.theverge.com/', 'T'),
        L('Ars Technica', 'https://arstechnica.com/', 'T'),
        L('TechCrunch', 'https://techcrunch.com/', 'T'),
      ]),
      S('Industry News', [
        L('Axios Pro', 'https://www.axios.com/pro', 'M'),
        L('The Information', 'https://www.theinformation.com/', 'M'),
        L('Business Insider Tech', 'https://www.businessinsider.com/tech', 'T'),
      ]),
      S('Product Launches', [
        L('Product Hunt', 'https://www.producthunt.com/', 'T'),
        L('BetaList', 'https://betalist.com/', 'T'),
        L('Launching Next', 'https://www.launchingnext.com/', 'T'),
      ]),
      S('Funding News', [
        L('Crunchbase News', 'https://news.crunchbase.com/', 'T'),
        L('TechCrunch Funding', 'https://techcrunch.com/category/venture/', 'T'),
        L('PitchBook News', 'https://pitchbook.com/news', 'M'),
      ]),
      S('AI Regulations', [
        L('EU AI Act (official)', 'https://artificialintelligenceact.eu/', 'T'),
        L('NIST AI RMF', 'https://www.nist.gov/itl/ai-risk-management-framework', 'T'),
        L('AI Policy Institute', 'https://theaipi.org/', 'T'),
      ]),
      S('Research Papers (Latest)', [
        L('arXiv Sanity Lite', 'https://arxiv-sanity-lite.com/', 'T'),
        L('Papers With Code', 'https://paperswithcode.com/', 'T'),
        L('AlphaXiv', 'https://www.alphaxiv.org/', 'T'),
      ]),
    ]),

    // ────────────────────────── 68. RESOURCES ──────────────────────────
    S('Resources', [
      S('Templates', [
        L('Notion Templates', 'https://www.notion.so/templates', 'T'),
        L('Airtable Universe', 'https://www.airtable.com/universe', 'T'),
        L('Canva Templates', 'https://www.canva.com/templates/', 'M'),
      ]),
      S('Prompt Library', [
        L('PromptHero', 'https://prompthero.com/', 'T'),
        L('PromptBase', 'https://promptbase.com/', 'M'),
        L('Anthropic Prompt Library', 'https://docs.anthropic.com/en/prompt-library/library', 'T'),
        L('FlowGPT', 'https://flowgpt.com/', 'T'),
      ]),
      S('Workflows', [
        L('Zapier Templates', 'https://zapier.com/templates', 'M'),
        L('Make Templates', 'https://www.make.com/en/templates', 'M'),
        L('n8n Workflows', 'https://n8n.io/workflows/', 'T'),
      ]),
      S('Cheat Sheets', [
        L('OverAPI', 'https://overapi.com/', 'T'),
        L('DevHints', 'https://devhints.io/', 'T'),
        L('Cheatography', 'https://www.cheatography.com/', 'T'),
      ]),
      S('APIs', [
        L('RapidAPI', 'https://rapidapi.com/', 'R'),
        L('Public APIs (GitHub)', 'https://github.com/public-apis/public-apis', 'T'),
        L('APIs.guru', 'https://apis.guru/', 'T'),
      ]),
      S('Datasets', [
        L('Kaggle Datasets', 'https://www.kaggle.com/datasets', 'T'),
        L('Hugging Face Datasets', 'https://huggingface.co/datasets', 'T'),
        L('Google Dataset Search', 'https://datasetsearch.research.google.com/', 'T'),
      ]),
      S('Chrome / Browser Extensions', [
        L('Chrome Web Store', 'https://chromewebstore.google.com/', 'T'),
        L('Firefox Add-ons', 'https://addons.mozilla.org/', 'T'),
        L('MaxAI.me', 'https://www.maxai.me/', 'M'),
        L('Merlin AI (extension)', 'https://merlin.foyer.work/', 'M'),
      ]),
      S('GitHub / Open Source Projects', [
        L('GitHub Trending', 'https://github.com/trending', 'T'),
        L('Awesome AI (list)', 'https://github.com/owainlewis/awesome-artificial-intelligence', 'T'),
        L('Awesome ChatGPT', 'https://github.com/awesome-chatgpt/awesome-chatgpt', 'T'),
      ]),
      S('Plugins', [
        L('ChatGPT Plugins (GPTs Store)', 'https://chatgpt.com/gpts', 'M'),
        L('Zapier CLI Plugins', 'https://platform.zapier.com/', 'T'),
        L('Obsidian Plugins', 'https://obsidian.md/plugins', 'T'),
      ]),
    ]),

    // ────────────────────────── 69. ENTERTAINMENT ──────────────────────────
    S('Entertainment', [
      S('Books', [
        L('Goodreads', 'https://www.goodreads.com/', 'T'),
        L('Amazon Books', 'https://www.amazon.com/books/', 'R'),
        L('Blinkist AI', 'https://www.blinkist.com/', 'M'),
        L('Shortform', 'https://www.shortform.com/', 'M'),
      ]),
      S('Movies', [
        L('Letterboxd', 'https://letterboxd.com/', 'T'),
        L('IMDb', 'https://www.imdb.com/', 'T'),
        L('Rotten Tomatoes', 'https://www.rottentomatoes.com/', 'T'),
        L('Movies.ai (recommender)', 'https://movies.ai/', 'T'),
      ]),
      S('TV', [
        L('JustWatch', 'https://www.justwatch.com/', 'T'),
        L('Reelgood', 'https://reelgood.com/', 'T'),
        L('IMDb TV', 'https://www.imdb.com/what-to-watch/', 'T'),
      ]),
      S('Podcasts', [
        L('Listen Notes', 'https://www.listennotes.com/', 'T'),
        L('Podchaser', 'https://www.podchaser.com/', 'T'),
        L('Spotify Podcasts', 'https://podcasters.spotify.com/', 'R'),
      ]),
      S('Games', [
        L('Steam', 'https://store.steampowered.com/', 'R'),
        L('itch.io', 'https://itch.io/', 'T'),
        L('IGN', 'https://www.ign.com/', 'T'),
        L('Twitch AI Games', 'https://www.twitch.tv/', 'R'),
      ]),
      S('Comics', [
        L('Webtoons', 'https://www.webtoons.com/', 'T'),
        L('Tapas', 'https://tapas.io/', 'T'),
        L('ComiXology', 'https://www.comixology.com/', 'M'),
      ]),
      S('Music (Discovery)', [
        L('Spotify', 'https://www.spotify.com/', 'T'),
        L('Apple Music', 'https://music.apple.com/', 'M'),
        L('Cyanite (AI tagging)', 'https://cyanite.ai/', 'M'),
        L('Tunebat', 'https://tunebat.com/', 'T'),
      ]),
      S('YouTube Channels', [
        L('AI Explained', 'https://www.youtube.com/@aiexplained-official', 'T'),
        L('Two Minute Papers', 'https://www.youtube.com/@TwoMinutePapers', 'T'),
        L('Matt Wolfe', 'https://www.youtube.com/@mreflow', 'T'),
      ]),
    ]),

    // ────────────────────────── 70. LOCAL & EVENTS ──────────────────────────
    S('Local & Events', [
      S('Local Businesses', [
        L('Google Business Profile', 'https://www.google.com/business/', 'R'),
        L('Yelp AI', 'https://www.yelp.com/', 'R'),
        L('Bing Places', 'https://www.bingplaces.com/', 'R'),
      ]),
      S('Events (General)', [
        L('Eventbrite', 'https://www.eventbrite.com/', 'R'),
        L('Meetup', 'https://www.meetup.com/', 'R'),
        L('Luma', 'https://lu.ma/', 'R'),
      ]),
      S('Meetups', [
        L('Meetup', 'https://www.meetup.com/', 'R'),
        L('AI Meetups Directory', 'https://www.meetup.com/topics/artificial-intelligence/', 'R'),
      ]),
      S('Conferences', [
        L('NeurIPS', 'https://neurips.cc/', 'R'),
        L('ICML', 'https://icml.cc/', 'R'),
        L('CVPR', 'https://cvpr.thecvf.com/', 'R'),
        L('AI Engineer Summit', 'https://www.ai.engineer/', 'R'),
      ]),
      S('Workshops', [
        L('Coursera Live', 'https://www.coursera.org/', 'M'),
        L('Maven', 'https://maven.com/', 'M'),
        L("O'Reilly Live Events", 'https://www.oreilly.com/live-events/', 'M'),
      ]),
      S('Hackathons', [
        L('Devpost', 'https://devpost.com/', 'T'),
        L('MLH (Major League Hacking)', 'https://mlh.io/', 'T'),
        L('Kaggle Competitions', 'https://www.kaggle.com/competitions', 'T'),
        L('lablab.ai (AI Hackathons)', 'https://lablab.ai/', 'T'),
      ]),
    ]),

    // ────────────────────────── 71. SHOPPING & DEALS ──────────────────────────
    S('Shopping & Deals', [
      S('Software Discounts / Lifetime Deals', [
        L('AppSumo', 'https://appsumo.com/', 'M'),
        L('StackSocial', 'https://stacksocial.com/', 'M'),
        L('DealFuel', 'https://www.dealfuel.com/', 'M'),
        L('Prime Club', 'https://theprimeclub.com/', 'M'),
      ]),
      S('AI Gadgets / Hardware', [
        L('Rabbit R1', 'https://www.rabbit.tech/', 'M'),
        L('Humane AI Pin', 'https://humane.com/', 'M'),
        L('Meta Ray-Ban', 'https://www.meta.com/smart-glasses/', 'M'),
        L('NVIDIA Jetson', 'https://www.nvidia.com/en-us/autonomous-machines/embedded-systems/', 'M'),
      ]),
      S('Coupons / Deals', [
        L('Honey', 'https://www.joinhoney.com/', 'T'),
        L('Rakuten', 'https://www.rakuten.com/', 'T'),
        L('Slickdeals', 'https://slickdeals.net/', 'T'),
      ]),
    ]),

    // ────────────────────────── 72. PERSONAL (LIFE HACKS) ──────────────────────────
    S('Personal / Life Hacks', [
      S('Productivity (Personal)', [
        L('Notion', 'https://www.notion.so/', 'T'),
        L('Todoist', 'https://todoist.com/', 'T'),
        L('Motion', 'https://www.usemotion.com/', 'M'),
        L('Reclaim.ai', 'https://reclaim.ai/', 'M'),
      ]),
      S('Life Hacks', [
        L('Life Pro Tips (Reddit)', 'https://www.reddit.com/r/LifeProTips/', 'T'),
        L('Lifehacker', 'https://lifehacker.com/', 'T'),
        L('WikiHow', 'https://www.wikihow.com/', 'T'),
      ]),
      S('DIY (Personal)', [
        L('Instructables', 'https://www.instructables.com/', 'T'),
        L('Make Magazine', 'https://makezine.com/', 'T'),
        L('r/DIY', 'https://www.reddit.com/r/DIY/', 'T'),
      ]),
      S('Hobbies', [
        L('Skillshare', 'https://www.skillshare.com/', 'M'),
        L('Domestika', 'https://www.domestika.org/', 'M'),
        L('MasterClass', 'https://www.masterclass.com/', 'M'),
      ]),
    ]),

    // ────────────────────── CREATORS & INFLUENCERS ──────────────────────
    S('Creators & Influencers', [
      S('AI YouTubers', [
        { name: 'Matt Wolfe', _route: 'creator', _slug: 'creator-matt-wolfe', type: 'T' },
        { name: 'Wes Roth', _route: 'creator', _slug: 'creator-wes-roth', type: 'T' },
        { name: 'MattVidPro AI', _route: 'creator', _slug: 'creator-mattvidpro-ai', type: 'T' },
        { name: 'AI Search', _route: 'creator', _slug: 'creator-ai-search', type: 'T' },
        { name: 'bycloud', _route: 'creator', _slug: 'creator-bycloud', type: 'T' },
        { name: 'Yannic Kilcher', _route: 'creator', _slug: 'creator-yannic-kilcher', type: 'T' },
        { name: 'Two Minute Papers', _route: 'creator', _slug: 'creator-two-minute-papers', type: 'T' },
        { name: 'TheAIGrid', _route: 'creator', _slug: 'creator-theaigrid', type: 'T' },
        { name: 'All About AI', _route: 'creator', _slug: 'creator-all-about-ai', type: 'T' },
        { name: 'Fireship', _route: 'creator', _slug: 'creator-fireship', type: 'T' },
        { name: 'Bilawal Sidhu', _route: 'creator', _slug: 'creator-bilawal-sidhu', type: 'T' },
        { name: 'Aitrepreneur', _route: 'creator', _slug: 'creator-aitrepreneur', type: 'T' },
      ]),
      S('TikTok Creators', [
        { name: 'Riley Brown', _route: 'creator', _slug: 'creator-riley-brown', type: 'T' },
      ]),
      S('Newsletter Writers', [
        { name: 'Rowan Cheung (The Rundown)', _route: 'creator', _slug: 'creator-rowan-cheung', type: 'T' },
        { name: 'Everyday AI (Jordan Wilson)', _route: 'creator', _slug: 'creator-everyday-ai-jordan-wilson', type: 'T' },
      ]),
      S('AI Builder / Agent educators', [
        { name: 'Nick Saraev', _route: 'creator', _slug: 'creator-nick-saraev', type: 'T' },
        { name: 'Liam Ottley', _route: 'creator', _slug: 'creator-liam-ottley', type: 'T' },
        { name: 'David Ondrej', _route: 'creator', _slug: 'creator-david-ondrej', type: 'T' },
      ]),
      S('Thought Leaders on X', [
        { name: 'Bindu Reddy', _route: 'creator', _slug: 'creator-bindu-reddy', type: 'T' },
        { name: 'Sunny Madra', _route: 'creator', _slug: 'creator-sunny-madra', type: 'T' },
      ]),
    ]),

    // ────────────────────────── COMPANIES ──────────────────────────
    S('Companies', [
      S('AI Labs', [
        { name: 'OpenAI', _route: 'company', _slug: 'company-openai', type: 'T' },
        { name: 'Anthropic', _route: 'company', _slug: 'company-anthropic', type: 'T' },
        { name: 'Google DeepMind', _route: 'company', _slug: 'company-google-deepmind', type: 'T' },
        { name: 'Meta AI', _route: 'company', _slug: 'company-meta-ai', type: 'T' },
        { name: 'Microsoft AI', _route: 'company', _slug: 'company-microsoft-ai', type: 'T' },
        { name: 'Stability AI', _route: 'company', _slug: 'company-stability-ai', type: 'T' },
      ]),
      S('AI Platforms', [
        { name: 'Hugging Face', _route: 'company', _slug: 'company-hugging-face', type: 'T' },
        { name: 'Perplexity AI', _route: 'company', _slug: 'company-perplexity-ai', type: 'T' },
      ]),
    ]),

    // ────────────────────────── DISCOVERY ──────────────────────────
    S('Discovery', [
      { name: "Editor's Picks", _route: 'discover', _slug: 'editors_pick', type: 'T' },
      { name: 'Trending on TikTok', _route: 'discover', _slug: 'trending_tiktok', type: 'T' },
      { name: 'Trending on YouTube', _route: 'discover', _slug: 'trending_youtube', type: 'T' },
      { name: 'Hidden Gems', _route: 'discover', _slug: 'hidden_gem', type: 'T' },
      { name: 'Weird AI', _route: 'discover', _slug: 'weird_ai', type: 'D' },
      { name: 'Beginner Friendly', _route: 'discover', _slug: 'beginner_friendly', type: 'T' },
      { name: 'Rising Creators', _route: 'discover', _slug: 'rising_creator', type: 'T' },
      { name: 'New & Notable', _route: 'discover', _slug: 'new', type: 'R' },
      { name: 'Most Popular', _route: 'discover', _slug: 'popular', type: 'M' },
    ]),



  ],
};

export const legend = [
  { code: '(T)', desc: 'Free / open tool' },
  { code: '(R)', desc: 'Requires registration' },
  { code: '(M)', desc: 'Paid or freemium' },
  { code: '(D)', desc: 'Dangerous / underground — use at your own risk' },
];
